import {useCrmTab,useCrmFocus} from '../../hooks/useCrmSection';
import RefillBillingSettings from '../../components/refills/RefillBillingSettings';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Alert,
  IconButton,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Collapse,
  Radio,
  RadioGroup,
  FormControlLabel,
  Switch,
  useMediaQuery,
  useTheme,
  Menu as MuiMenu,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Receipt as ReceiptIcon,
  Description as DescriptionIcon,
  Propane as PropaneIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Add as AddIcon,
  Euro as EuroIcon,
  Speed as MeterIcon,
  Build as BuildIcon,
  Delete as DeleteIcon,
  Forum as ForumIcon,
  Send as SendIcon,
  Inbox as InboxIcon,
  Folder as FolderIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Description as WordIcon,
  FolderZip as ZipIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Payment as PaymentIcon,
  NotificationsOff as NotificationsOffIcon,
  EventRepeat as EventRepeatIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Smartphone as SmartphoneIcon,
  NoteAdd as NoteAddIcon,
  DeleteOutline as DeleteOutlineIcon,
  Save as SaveIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { Checkbox as MuiCheckbox } from '@mui/material';
import { Link } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { customersApi, metersApi, emailsApi, customerDocumentsApi, invoicesApi } from '../../services/api';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { formatNuf } from '../../utils/formatNuf';
import { formatCustomerName } from '../../utils/formatCustomerName';
import MobileCardList from '../../components/MobileCardList';

// Formatta importo in Euro
const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
  }).format(value || 0);
};

const paymentNoteSx = {
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

const DEFAULT_PRIVATE_CONTRACT_USAGE = 'uso domestico (riscaldamento, produzione acqua calda sanitaria e cottura cibi)';
const DEFAULT_ENTITY_CONTRACT_USAGE = 'uso ente/associazione';
const DEFAULT_BUSINESS_CONTRACT_USAGE = 'uso commerciale';
const BUSINESS_ENTITY_USAGE_PATTERN = /associazione|ente|fondazione|comitato|cooperativa sociale|onlus|aps|asd|ets/i;

const CONTRACT_USAGE_OPTIONS = [
  { value: DEFAULT_PRIVATE_CONTRACT_USAGE, label: 'Uso domestico (riscaldamento/ACS/cottura)' },
  { value: 'uso cottura cibi', label: 'Uso cottura cibi' },
  { value: 'uso riscaldamento', label: 'Uso riscaldamento' },
  { value: 'uso acqua calda sanitaria', label: 'Uso acqua calda sanitaria' },
  { value: 'uso condominiale/residenziale', label: 'Uso condominiale/residenziale' },
  { value: DEFAULT_BUSINESS_CONTRACT_USAGE, label: 'Uso commerciale' },
  { value: 'uso uffici/studi professionali', label: 'Uso uffici/studi professionali' },
  { value: 'uso artigianale', label: 'Uso artigianale' },
  { value: 'uso industriale', label: 'Uso industriale' },
  { value: 'uso agricolo', label: 'Uso agricolo' },
  { value: 'uso ristorazione/cucina professionale', label: 'Uso ristorazione/cucina professionale' },
  { value: 'uso ricettivo/turistico', label: 'Uso ricettivo/turistico' },
  { value: DEFAULT_ENTITY_CONTRACT_USAGE, label: 'Uso ente/associazione' },
  { value: 'uso pubblico/istituzionale', label: 'Uso pubblico/istituzionale' },
  { value: 'altro uso non domestico', label: 'Altro uso non domestico' },
];

const getDefaultUsageForCustomer = (customer) => {
  if (!customer || customer.customer_type !== 'business') return DEFAULT_PRIVATE_CONTRACT_USAGE;
  const name = customer.business_name || customer.ragione_sociale || '';
  return BUSINESS_ENTITY_USAGE_PATTERN.test(name) ? DEFAULT_ENTITY_CONTRACT_USAGE : DEFAULT_BUSINESS_CONTRACT_USAGE;
};

const getDefaultContractParams = (customer) => ({
  prezzo_litro: '',
  aliquota_iva: '22',
  quota_fissa_mensile: '0',
  deposito_cauzionale: '0',
  spese_allaccio: '0',
  durata_contratto_anni: '5',
  uso_fornitura: getDefaultUsageForCustomer(customer),
  litri_stimati_anno: '1000'
});

// Tab Panel
const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
  </div>
);

const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [tabValue, setTabValue] = useCrmTab(['fatture','contatori','interventi','comunicazioni','documenti']);
  const [sectionParams] = useSearchParams();
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  
  // Stati per dialog intervento
  const [interventionDialogOpen, setInterventionDialogOpen] = useState(false);
  const [interventionForm, setInterventionForm] = useState({
    meter_id: '',
    dicitura: '',
    descrizione: '',
    importo: '',
    data_intervento: new Date().toISOString().split('T')[0],
    note: ''
  });

  // Stati per upload documenti
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileDescription, setFileDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  
  // Stati per fatture espanse
  const [expandedInvoices, setExpandedInvoices] = useState([]);
  const [paymentsData, setPaymentsData] = useState({});

  const getPaymentMethodLabel = (paymentMethod) => {
    switch (paymentMethod) {
      case 'cash':
        return 'Contanti';
      case 'bank_transfer':
        return 'Bonifico';
      case 'bank_draft':
        return 'Assegno';
      case 'rid':
        return 'RID';
      default:
        return paymentMethod || '-';
    }
  };

  const loadInvoicePaymentsHistory = (invoiceId) => {
    if (paymentsData[invoiceId]) {
      return;
    }

    invoicesApi
      .getPaymentsHistory(invoiceId)
      .then((res) => {
        setPaymentsData((prev) => ({
          ...prev,
          [invoiceId]: res.data,
        }));
      })
      .catch((err) => {
        console.error('Errore caricamento pagamenti:', err);
      });
  };

  const toggleInvoiceExpansion = (invoiceId) => {
    setExpandedInvoices((prev) => {
      const isExpanded = prev.includes(invoiceId);

      if (isExpanded) {
        return prev.filter((currentId) => currentId !== invoiceId);
      }

      loadInvoicePaymentsHistory(invoiceId);
      return [...prev, invoiceId];
    });
  };
  
  // Stati per estratto conto
  const [accountStatementDialogOpen, setAccountStatementDialogOpen] = useState(false);
  const [accountStatementLoading, setAccountStatementLoading] = useState(false);
  const [accountStatementEmail, setAccountStatementEmail] = useState('');
  const [selectedFromInvoiceId, setSelectedFromInvoiceId] = useState(null); // null = tutte le fatture

  // Stati per piano di rateizzazione
  const [installmentDialogOpen, setInstallmentDialogOpen] = useState(false);
  const [installmentLoading, setInstallmentLoading] = useState(false);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [installmentRecipientType, setInstallmentRecipientType] = useState('intestatario');
  const [installmentRecipientData, setInstallmentRecipientData] = useState({ name: '', surname: '', cf: '', document: '' });
  const [installmentNumRates, setInstallmentNumRates] = useState(3);
  const [installmentRates, setInstallmentRates] = useState([]);
  const [installmentIncludeMora, setInstallmentIncludeMora] = useState(true);

  // Stati per invio email piano rateizzazione
  const [installmentEmailDialogOpen, setInstallmentEmailDialogOpen] = useState(false);
  const [installmentEmailLoading, setInstallmentEmailLoading] = useState(false);
  const [installmentEmailSending, setInstallmentEmailSending] = useState(false);
  const [installmentEmailAddress, setInstallmentEmailAddress] = useState('');
  const [installmentEmailPlanId, setInstallmentEmailPlanId] = useState(null);
  const [installmentEmailSubject, setInstallmentEmailSubject] = useState('');
  const [installmentEmailBody, setInstallmentEmailBody] = useState('');
  const [installmentEmailError, setInstallmentEmailError] = useState(null);
  const [installmentEmailPreview, setInstallmentEmailPreview] = useState(null);

  // Stati per eliminazione definitiva cliente
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Stati per contratto GPL
  const [contractDialogOpen, setContractDialogOpen] = useState(false);
  const [contractLoading, setContractLoading] = useState(false);
  const [contractEmail, setContractEmail] = useState('');

  const handleOpenWhatsappChat = () => {
    const phoneNumber = String(customer?.whatsapp_number || '').trim();
    if (!phoneNumber) return;

    const contactName = formatCustomerName(customer, phoneNumber);

    navigate(`/crm/whatsapp?phone=${encodeURIComponent(phoneNumber)}`, {
      state: {
        phoneNumber,
        contactName,
        customer: customer?.id
          ? {
              id: customer.id,
              cod_cli: customer.cod_cli,
              nome: contactName,
            }
          : null,
      },
    });
  };
  const [selectedMeterId, setSelectedMeterId] = useState(null);
  const [contractAction, setContractAction] = useState('download'); // 'download' o 'send'
  
  // Stati per parametri contratto
  const [contractParamsDialogOpen, setContractParamsDialogOpen] = useState(false);
  const [contractParamsLoading, setContractParamsLoading] = useState(false);
  const [contractParamsMeterId, setContractParamsMeterId] = useState(null);
  
  // Valori di default per il contratto
  const [contractParamsForm, setContractParamsForm] = useState(() => getDefaultContractParams(null));
  
  // Query cliente
  const { data: customer, isLoading, error } = useQuery({
    queryKey: ['customer', id],
    queryFn: () => customersApi.getById(id).then(res => res.data?.data || res.data)
  });

  const defaultContractParams = React.useMemo(() => getDefaultContractParams(customer), [customer]);

  // Query fatture (tab index 0 o dialog estratto conto aperto)
  const { data: invoices } = useQuery({
    queryKey: ['customerInvoices', id],
    queryFn: () => customersApi.getInvoices(id).then(res => res.data?.data || []),
    enabled: tabValue === 0 || accountStatementDialogOpen
  });

  // Query contatori (tab index 1)
  const { data: meters } = useQuery({
    queryKey: ['customerMeters', id],
    queryFn: () => metersApi.getByCustomer(id).then(res => res.data || []),
    enabled: Boolean(id) && (tabValue === 1 || tabValue === 2 || interventionDialogOpen || contractDialogOpen || contractParamsDialogOpen)
  });

  // Query interventi extra (tab index 2)
  const { data: interventions, refetch: refetchInterventions } = useQuery({
    queryKey: ['customerInterventions', id],
    queryFn: () => customersApi.getInterventions(id).then(res => res.data?.data || []),
    enabled: tabValue === 2
  });

  // Mutation crea intervento
  const createInterventionMutation = useMutation({
    mutationFn: (data) => customersApi.createIntervention(id, data),
    onSuccess: () => {
      refetchInterventions();
      setInterventionDialogOpen(false);
      setInterventionForm({
        meter_id: '',
        dicitura: '',
        descrizione: '',
        importo: '',
        data_intervento: new Date().toISOString().split('T')[0],
        note: ''
      });
    }
  });

  // Se il cliente ha 1 solo contatore, pre-selezionalo automaticamente nel dialog intervento
  useEffect(() => {
    if (!interventionDialogOpen) return;
    const list = meters || [];
    if (list.length === 1) {
      setInterventionForm((prev) => {
        if (String(prev.meter_id || '') === String(list[0].id)) return prev;
        if (prev.meter_id) return prev;
        return { ...prev, meter_id: String(list[0].id) };
      });
    }
  }, [interventionDialogOpen, meters]);

  // Helper per caricare i dati del contatore con fallback ai default
  const loadMeterContractParams = (meter) => {
    return {
      prezzo_litro: meter.prezzo_litro ?? defaultContractParams.prezzo_litro,
      aliquota_iva: meter.aliquota_iva ?? defaultContractParams.aliquota_iva,
      quota_fissa_mensile: meter.quota_fissa_mensile ?? defaultContractParams.quota_fissa_mensile,
      deposito_cauzionale: meter.deposito_cauzionale ?? defaultContractParams.deposito_cauzionale,
      spese_allaccio: meter.spese_allaccio ?? defaultContractParams.spese_allaccio,
      durata_contratto_anni: meter.durata_contratto_anni ?? defaultContractParams.durata_contratto_anni,
      uso_fornitura: meter.uso_fornitura || defaultContractParams.uso_fornitura,
      litri_stimati_anno: meter.litri_stimati_anno ?? defaultContractParams.litri_stimati_anno
    };
  };

  // Pre-seleziona contatore nel dialog parametri contratto quando i meters sono caricati
  useEffect(() => {
    if (!contractParamsDialogOpen) return;
    const list = meters || [];
    if (list.length === 1 && !contractParamsMeterId) {
      const meter = list[0];
      setContractParamsMeterId(meter.id);
      setContractParamsForm(loadMeterContractParams(meter));
    }
  }, [contractParamsDialogOpen, meters, contractParamsMeterId]);

  // Mutation elimina intervento
  const deleteInterventionMutation = useMutation({
    mutationFn: (interventionId) => customersApi.deleteIntervention(id, interventionId),
    onSuccess: () => {
      refetchInterventions();
    }
  });

  // Query saldo
  const { data: balance } = useQuery({
    queryKey: ['customerBalance', id],
    queryFn: () => customersApi.getBalance(id).then(res => res.data?.data || {})
  });

  // Query piani di rateizzazione
  const { data: installmentPlans = [], refetch: refetchInstallmentPlans } = useQuery({
    queryKey: ['customerInstallmentPlans', id],
    queryFn: () => customersApi.getInstallmentPlans(id).then(res => res.data?.data || []),
    enabled: Boolean(id)
  });

  // Controlla se ci sono piani attivi
  const hasActiveInstallmentPlan = installmentPlans.some(p => p.status === 'attivo');

  // Helper: genera array rate distribuite equamente
  const generateRatesArray = (total, numRates, startDate) => {
    const rateAmount = Math.floor((total / numRates) * 100) / 100;
    const remainder = Math.round((total - rateAmount * numRates) * 100) / 100;
    const start = startDate ? new Date(startDate) : new Date();
    
    return Array.from({ length: numRates }, (_, i) => {
      const dueDate = new Date(start);
      dueDate.setMonth(dueDate.getMonth() + i + 1);
      return {
        rate_number: i + 1,
        amount: i === numRates - 1 ? rateAmount + remainder : rateAmount,
        due_date: dueDate.toISOString().split('T')[0]
      };
    });
  };

  // Calcolo interessi di mora per una fattura
  const MORA_RATE = 0.025; // 2.5% annuo (default)
  const calcolaMora = (inv) => {
    const saldo = parseFloat(inv.f_totale || 0) - parseFloat(inv.f_paga_imp || 0);
    if (saldo <= 0 || !inv.f_ult_gior) return 0;
    const scadenza = new Date(inv.f_ult_gior);
    const oggi = new Date();
    if (oggi <= scadenza) return 0;
    const giorniRitardo = Math.floor((oggi.getTime() - scadenza.getTime()) / (24 * 60 * 60 * 1000));
    return Math.round(saldo * MORA_RATE * (giorniRitardo / 365) * 100) / 100;
  };

  // Calcolo totale fatture selezionate per rateizzazione (saldo + mora condizionale)
  const selectedInvoicesTotal = (invoices || [])
    .filter(inv => selectedInvoiceIds.includes(inv.id))
    .reduce((sum, inv) => {
      const saldo = parseFloat(inv.f_totale || 0) - parseFloat(inv.f_paga_imp || 0);
      const mora = installmentIncludeMora ? calcolaMora(inv) : 0;
      return sum + Math.max(saldo, 0) + mora;
    }, 0);

  // Totale sola mora per le fatture selezionate
  const selectedInvoicesMora = installmentIncludeMora
    ? (invoices || [])
        .filter(inv => selectedInvoiceIds.includes(inv.id))
        .reduce((sum, inv) => sum + calcolaMora(inv), 0)
    : 0;

  // Aggiorna rate quando cambia il numero o le fatture selezionate
  const handleUpdateRates = (numRates, total) => {
    if (total <= 0 || numRates <= 0) {
      setInstallmentRates([]);
      return;
    }
    setInstallmentRates(generateRatesArray(total, numRates));
  };

  // Query comunicazioni (email + whatsapp)
  const { data: communications = [], isLoading: communicationsLoading } = useQuery({
    queryKey: ['customerCommunications', id],
    queryFn: () => customersApi.getCommunications(id).then(res => res.data?.data?.messages || [])
  });

  // Query documenti cliente
  const { data: documents = [], isLoading: documentsLoading, refetch: refetchDocuments } = useQuery({
    queryKey: ['customerDocuments', id],
    queryFn: () => customerDocumentsApi.getAll(id).then(res => res.data?.data || []),
    enabled: tabValue === 4 // Documenti è ora la tab index 4
  });

  // Mutation elimina documento
  const deleteDocumentMutation = useMutation({
    mutationFn: (documentId) => customerDocumentsApi.delete(id, documentId),
    onSuccess: () => {
      refetchDocuments();
    }
  });

  // Mutation aggiorna esclusione solleciti
  const updateExcludeRemindersMutation = useMutation({
    mutationFn: (excludeFromReminders) => customersApi.update(id, { exclude_from_reminders: excludeFromReminders }),
    onSuccess: () => {
      queryClient.invalidateQueries(['customer', id]);
    }
  });

  // ============================================
  // NOTE CLIENTE
  // ============================================
  const [newNoteText, setNewNoteText] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  const [deleteNoteDialogOpen, setDeleteNoteDialogOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);

  // Query note cliente
  const { data: customerNotes = [], refetch: refetchNotes } = useQuery({
    queryKey: ['customer-notes', id],
    queryFn: () => customersApi.getNotes(id).then(res => res.data?.data || []),
    enabled: Boolean(id)
  });

  const {
    data: customerAiSummary,
    isLoading: customerAiSummaryLoading,
    error: customerAiSummaryError,
  } = useQuery({
    queryKey: ['customer-ai-summary', id],
    queryFn: () => customersApi.getAiSummary(id).then(res => res.data?.data || null),
    enabled: Boolean(id),
    staleTime: 5 * 60 * 1000,
  });

  const refreshCustomerAiSummaryMutation = useMutation({
    mutationFn: () => customersApi.refreshAiSummary(id).then(res => res.data?.data || null),
    onSuccess: (data) => {
      queryClient.setQueryData(['customer-ai-summary', id], data);
    }
  });

  // Mutation crea nota
  const createNoteMutation = useMutation({
    mutationFn: (text) => customersApi.createNote(id, { text }),
    onSuccess: () => {
      refetchNotes();
      queryClient.invalidateQueries({ queryKey: ['customer-ai-summary', id] });
      setNewNoteText('');
    }
  });

  // Mutation modifica nota
  const updateNoteMutation = useMutation({
    mutationFn: ({ noteId, text }) => customersApi.updateNote(id, noteId, { text }),
    onSuccess: () => {
      refetchNotes();
      queryClient.invalidateQueries({ queryKey: ['customer-ai-summary', id] });
      setEditingNoteId(null);
      setEditingNoteText('');
    }
  });

  // Mutation elimina nota
  const deleteNoteMutation = useMutation({
    mutationFn: (noteId) => customersApi.deleteNote(id, noteId),
    onSuccess: () => {
      refetchNotes();
      queryClient.invalidateQueries({ queryKey: ['customer-ai-summary', id] });
      setDeleteNoteDialogOpen(false);
      setNoteToDelete(null);
    }
  });

  // Handler upload documento
  const handleUploadDocument = async () => {
    if (!selectedFile) return;
    
    setUploading(true);
    try {
      await customerDocumentsApi.upload(id, selectedFile, fileDescription);
      refetchDocuments();
      setUploadDialogOpen(false);
      setSelectedFile(null);
      setFileDescription('');
    } catch (error) {
      console.error('Errore upload documento:', error);
      alert('Errore durante il caricamento del documento');
    } finally {
      setUploading(false);
    }
  };

  // Handler download documento
  useCrmFocus(sectionParams.get('record') ? 'customer-document-'+sectionParams.get('record') : null, tabValue===4 && !isLoading && !documentsLoading);

  const handleDownloadDocument = async (doc) => {
    try {
      const response = await customerDocumentsApi.download(id, doc.id);
      const blob = new Blob([response.data], { type: doc.mimetype });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Errore download documento:', error);
      alert('Errore durante il download del documento');
    }
  };

  // Handler visualizza documento
  const handleViewDocument = async (doc) => {
    try {
      const response = await customerDocumentsApi.view(id, doc.id);
      const blob = new Blob([response.data], { type: doc.mimetype });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Errore visualizzazione documento:', error);
      alert('Errore durante la visualizzazione del documento');
    }
  };

  // Handler eliminazione definitiva cliente
  const handleDeletePermanent = async () => {
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await customersApi.deletePermanent(id);
      queryClient.invalidateQueries(['customers']);
      navigate('/crm/clienti');
    } catch (error) {
      console.error('Errore eliminazione cliente:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Errore durante l\'eliminazione del cliente';
      setDeleteError(errorMessage);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Handler apertura dialog contratto
  const handleOpenContractDialog = (action) => {
    setContractAction(action);
    setContractEmail(customer?.email || customer?.pec || '');
    // Se c'è un solo contatore, selezionalo automaticamente
    if (meters?.length === 1) {
      setSelectedMeterId(meters[0].id);
    } else {
      setSelectedMeterId(null);
    }
    setContractDialogOpen(true);
  };

  // Handler download contratto PDF
  const handleDownloadContract = async () => {
    setContractLoading(true);
    try {
      const response = await customersApi.getContractPdf(id, selectedMeterId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Per privati usa cognome + nome, per aziende ragione sociale
      const customerFullName = customer.business_name 
        ? customer.business_name 
        : `${customer.last_name || ''} ${customer.first_name || ''}`.trim() || 'cliente';
      const customerName = customerFullName.replace(/[^a-zA-Z0-9àèéìòùÀÈÉÌÒÙ\s]/g, '').replace(/\s+/g, '_');
      a.download = `Contratto_GPL_${customerName}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setContractDialogOpen(false);
    } catch (error) {
      console.error('Errore download contratto:', error);
      const errorMsg = error.response?.data?.message || 'Errore durante il download del contratto';
      alert(errorMsg);
    } finally {
      setContractLoading(false);
    }
  };

  // Handler invio contratto via email
  const handleSendContract = async () => {
    if (!contractEmail) {
      alert('Inserire un indirizzo email');
      return;
    }
    setContractLoading(true);
    try {
      await customersApi.sendContract(id, selectedMeterId, contractEmail);
      alert(`Contratto inviato con successo a ${contractEmail}`);
      setContractDialogOpen(false);
    } catch (error) {
      console.error('Errore invio contratto:', error);
      const errorMsg = error.response?.data?.message || 'Errore durante l\'invio del contratto';
      alert(errorMsg);
    } finally {
      setContractLoading(false);
    }
  };

  // Handler apertura dialog parametri contratto
  const handleOpenContractParams = () => {
    // Se c'è un solo contatore, carica i suoi dati
    if (meters?.length === 1) {
      const meter = meters[0];
      setContractParamsMeterId(meter.id);
      setContractParamsForm(loadMeterContractParams(meter));
    } else {
      setContractParamsMeterId(null);
      setContractParamsForm(defaultContractParams);
    }
    setContractParamsDialogOpen(true);
  };

  // Handler selezione contatore nel dialog parametri
  const handleSelectMeterForParams = (meterId) => {
    const meter = meters?.find(m => m.id === parseInt(meterId));
    if (meter) {
      setContractParamsMeterId(meter.id);
      setContractParamsForm(loadMeterContractParams(meter));
    }
  };

  // Handler salvataggio parametri contratto
  const handleSaveContractParams = async () => {
    if (!contractParamsMeterId) {
      alert('Selezionare un contatore');
      return;
    }
    setContractParamsLoading(true);
    try {
      // Prepara i dati convertendo stringhe vuote in null e stringhe in numeri
      const dataToSave = {
        prezzo_litro: contractParamsForm.prezzo_litro !== '' ? parseFloat(contractParamsForm.prezzo_litro) : null,
        aliquota_iva: contractParamsForm.aliquota_iva !== '' ? parseFloat(contractParamsForm.aliquota_iva) : null,
        quota_fissa_mensile: contractParamsForm.quota_fissa_mensile !== '' ? parseFloat(contractParamsForm.quota_fissa_mensile) : null,
        deposito_cauzionale: contractParamsForm.deposito_cauzionale !== '' ? parseFloat(contractParamsForm.deposito_cauzionale) : null,
        spese_allaccio: contractParamsForm.spese_allaccio !== '' ? parseFloat(contractParamsForm.spese_allaccio) : null,
        durata_contratto_anni: contractParamsForm.durata_contratto_anni !== '' ? parseInt(contractParamsForm.durata_contratto_anni) : null,
        uso_fornitura: contractParamsForm.uso_fornitura || null,
        litri_stimati_anno: contractParamsForm.litri_stimati_anno !== '' ? parseInt(contractParamsForm.litri_stimati_anno) : null
      };
      
      console.log('Salvataggio parametri contratto:', dataToSave);
      await metersApi.update(contractParamsMeterId, dataToSave);
      queryClient.invalidateQueries(['customerMeters', id]);
      alert('Parametri contratto salvati con successo');
      setContractParamsDialogOpen(false);
    } catch (error) {
      console.error('Errore salvataggio parametri:', error);
      const errorMsg = error.response?.data?.message || 'Errore durante il salvataggio dei parametri';
      alert(errorMsg);
    } finally {
      setContractParamsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Box>
        <Skeleton height={60} sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Skeleton height={300} />
          </Grid>
          <Grid item xs={12} md={8}>
            <Skeleton height={300} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (error || !customer) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/crm/clienti')}>
          Torna alla lista
        </Button>
        <Alert severity="error" sx={{ mt: 2 }}>
          Cliente non trovato
        </Alert>
      </Box>
    );
  }

  const fullName = customer.business_name || `${customer.first_name} ${customer.last_name}`;

  const metersCount = (meters || []).length;
  const requiresMeterSelection = metersCount > 1;
  const canSaveIntervention = Boolean(interventionForm.dicitura) && Boolean(interventionForm.importo) && (!requiresMeterSelection || Boolean(interventionForm.meter_id));

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 0 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 2, minWidth: 0, flex: 1 }}>
          <IconButton onClick={() => navigate('/crm/clienti')} size={isMobile ? 'small' : 'medium'}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant={isMobile ? 'h6' : 'h4'} fontWeight="bold" noWrap>
              {fullName}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
              <Chip
                icon={customer.customer_type === 'business' ? <BusinessIcon /> : <PersonIcon />}
                label={customer.customer_type === 'business' ? 'Azienda' : 'Privato'}
                size="small"
                variant="outlined"
              />
              <Chip
                label={customer.is_active ? 'Attivo' : 'Inattivo'}
                size="small"
                color={customer.is_active ? 'success' : 'default'}
              />
              {!isMobile && customer.legacy_code && (
                <Chip
                  label={`Cod. ${customer.legacy_code}`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
        </Box>
        {isMobile ? (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/crm/clienti/${id}/modifica`)}
            >
              Modifica
            </Button>
            <IconButton onClick={(e) => setMobileMenuAnchor(e.currentTarget)}>
              <MoreVertIcon />
            </IconButton>
            <MuiMenu
              anchorEl={mobileMenuAnchor}
              open={Boolean(mobileMenuAnchor)}
              onClose={() => setMobileMenuAnchor(null)}
            >
              <MenuItem onClick={() => { setMobileMenuAnchor(null); handleOpenContractDialog('download'); }}>
                <DownloadIcon sx={{ mr: 1 }} fontSize="small" /> Contratto
              </MenuItem>
              <MenuItem onClick={() => { setMobileMenuAnchor(null); handleOpenContractDialog('send'); }}>
                <SendIcon sx={{ mr: 1 }} fontSize="small" /> Invia Contratto
              </MenuItem>
              <MenuItem onClick={() => { setMobileMenuAnchor(null); handleOpenContractParams(); }}>
                <BuildIcon sx={{ mr: 1 }} fontSize="small" /> Parametri Contratto
              </MenuItem>
              <MenuItem onClick={() => { setMobileMenuAnchor(null); setDeleteDialogOpen(true); }}>
                <DeleteIcon sx={{ mr: 1 }} fontSize="small" color="error" /> Elimina
              </MenuItem>
            </MuiMenu>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Scarica Contratto GPL">
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => handleOpenContractDialog('download')}
              >
                Contratto
              </Button>
            </Tooltip>
            <Tooltip title="Invia Contratto via Email">
              <Button
                variant="outlined"
                color="primary"
                startIcon={<SendIcon />}
                onClick={() => handleOpenContractDialog('send')}
              >
                Invia Contratto
              </Button>
            </Tooltip>
            <Tooltip title="Modifica parametri del contratto GPL">
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<BuildIcon />}
                onClick={handleOpenContractParams}
              >
                Parametri Contratto
              </Button>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/crm/clienti/${id}/modifica`)}
            >
              Modifica
            </Button>
            <Tooltip title="Elimina definitivamente il cliente (irreversibile)">
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
              >
                Elimina
              </Button>
            </Tooltip>
          </Box>
        )}
      </Box>

      <RefillBillingSettings customerId={id}/>

      <Grid container spacing={isMobile ? 2 : 3}>
        {/* Dati Anagrafici */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Dati Anagrafici
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {customer.address && (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <LocationIcon color="action" />
                    <Box>
                      <Typography variant="body2">{customer.address}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {customer.postal_code} {customer.city} ({customer.province})
                      </Typography>
                    </Box>
                  </Box>
                )}
                
                {customer.phone && (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <PhoneIcon color="action" />
                    <Box>
                      <Typography variant="body2">{customer.phone}</Typography>
                      <Typography variant="caption" color="text.secondary">Telefono</Typography>
                    </Box>
                  </Box>
                )}
                
                {customer.mobile && (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <SmartphoneIcon color="action" />
                    <Box>
                      <Typography variant="body2">{customer.mobile}</Typography>
                      <Typography variant="caption" color="text.secondary">Cellulare</Typography>
                    </Box>
                  </Box>
                )}

                {customer.whatsapp_number && (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <WhatsAppIcon sx={{ color: '#25D366' }} />
                    <Box>
                      <Link
                        component="button"
                        type="button"
                        onClick={handleOpenWhatsappChat}
                        underline="hover"
                        variant="body2"
                        sx={{ color: '#25D366', fontWeight: 500, cursor: 'pointer', textAlign: 'left' }}
                      >
                        {customer.whatsapp_number}
                      </Link>
                      <Typography variant="caption" color="text.secondary" display="block">WhatsApp</Typography>
                    </Box>
                  </Box>
                )}
                
                {customer.email && (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <EmailIcon color="action" />
                    <Box>
                      <Typography variant="body2">{customer.email}</Typography>
                      <Typography variant="caption" color="text.secondary">Email</Typography>
                    </Box>
                  </Box>
                )}

                {customer.email_secondaria && (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <EmailIcon color="action" />
                    <Box>
                      <Typography variant="body2">{customer.email_secondaria}</Typography>
                      <Typography variant="caption" color="text.secondary">Email Secondaria</Typography>
                    </Box>
                  </Box>
                )}
                
                <Divider />
                
                {customer.fiscal_code && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Codice Fiscale
                    </Typography>
                    <Typography variant="body2" fontFamily="monospace">
                      {customer.fiscal_code}
                    </Typography>
                  </Box>
                )}
                
                {customer.vat_number && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Partita IVA
                    </Typography>
                    <Typography variant="body2" fontFamily="monospace">
                      {customer.vat_number}
                    </Typography>
                  </Box>
                )}
                
                {customer.sdi_code && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Codice SDI
                    </Typography>
                    <Typography variant="body2" fontFamily="monospace">
                      {customer.sdi_code}
                    </Typography>
                  </Box>
                )}
                
                {customer.pec && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      PEC
                    </Typography>
                    <Typography variant="body2">{customer.pec}</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Card Saldo */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Situazione Contabile
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Totale Fatturato
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formatCurrency(balance?.totalInvoiced)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Totale Pagato
                  </Typography>
                  <Typography variant="body1" fontWeight="medium" color="success.main">
                    {formatCurrency(balance?.totalPaid)}
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body1" fontWeight="bold">
                    Saldo Dovuto
                  </Typography>
                  <Typography 
                    variant="h6" 
                    fontWeight="bold" 
                    color={balance?.balance > 0 ? 'error.main' : 'success.main'}
                  >
                    {formatCurrency(balance?.balance)}
                  </Typography>
                </Box>
                
                {balance?.overdueAmount > 0 && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    Scaduto: {formatCurrency(balance?.overdueAmount)}
                  </Alert>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Card Preferenze Fatturazione */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Preferenze Fatturazione
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Invio fattura via Email
                  </Typography>
                  <Chip 
                    label={customer.invio_email ? 'Sì' : 'No'} 
                    color={customer.invio_email ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Modalità Pagamento
                  </Typography>
                  <Chip 
                    label={
                      customer.modalita_pagamento === 'bollettino' ? 'Bollettino Postale' :
                      customer.modalita_pagamento === 'bonifico' ? 'Bonifico Bancario' :
                      customer.modalita_pagamento === 'rid_sepa' ? 'RID SEPA' :
                      'Bollettino Postale'
                    }
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                </Box>
                {customer.modalita_pagamento === 'rid_sepa' && customer.iban && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      IBAN
                    </Typography>
                    <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                      {customer.iban}
                    </Typography>
                  </Box>
                )}
                
                <Divider />
                
                {/* Rateizzazione in corso */}
                {hasActiveInstallmentPlan && (
                  <Box sx={{ mb: 1 }}>
                    <Chip
                      icon={<EventRepeatIcon />}
                      label={`Rateizzazione in corso (${installmentPlans.filter(p => p.status === 'attivo').length})`}
                      color="info"
                      variant="outlined"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                )}

                {/* Esclusione Solleciti */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <NotificationsOffIcon color={customer.exclude_from_reminders ? 'warning' : 'action'} fontSize="small" />
                    <Typography variant="body2" color="text.secondary">
                      Escludi da solleciti automatici
                    </Typography>
                  </Box>
                  <Switch
                    checked={customer.exclude_from_reminders || false}
                    onChange={(e) => updateExcludeRemindersMutation.mutate(e.target.checked)}
                    disabled={updateExcludeRemindersMutation.isPending}
                    color="warning"
                    size="small"
                  />
                </Box>
                {customer.exclude_from_reminders && (
                  <Alert severity="warning" sx={{ py: 0.5 }}>
                    <Typography variant="caption">
                      Questo cliente non riceverà promemoria pagamento e solleciti di distacco automatici.
                    </Typography>
                  </Alert>
                )}
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 1.5 }}>
                <Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Riassunto AI
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Sintesi automatica di email, WhatsApp e note cliente.
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => refreshCustomerAiSummaryMutation.mutate()}
                  disabled={refreshCustomerAiSummaryMutation.isPending}
                >
                  {refreshCustomerAiSummaryMutation.isPending ? 'Aggiornamento...' : 'Aggiorna'}
                </Button>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {customerAiSummaryLoading ? (
                <Box>
                  <Skeleton variant="text" height={28} sx={{ mb: 1 }} />
                  <Skeleton variant="text" height={22} />
                  <Skeleton variant="text" height={22} width="88%" />
                  <Skeleton variant="rounded" height={72} sx={{ mt: 1.5 }} />
                </Box>
              ) : customerAiSummaryError ? (
                <Alert severity="warning">
                  {customerAiSummaryError?.response?.data?.message || 'Impossibile generare il riassunto AI in questo momento.'}
                </Alert>
              ) : customerAiSummary ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {customerAiSummary.warning && (
                    <Alert severity="info">
                      Visualizzato l'ultimo riassunto disponibile. Motivo: {customerAiSummary.warning}
                    </Alert>
                  )}

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Chip label={`Email ${customerAiSummary.source_summary?.email || 0}`} size="small" variant="outlined" />
                    <Chip label={`WhatsApp ${customerAiSummary.source_summary?.whatsapp || 0}`} size="small" variant="outlined" />
                    <Chip label={`Note ${customerAiSummary.source_summary?.notes || 0}`} size="small" variant="outlined" />
                  </Box>

                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {customerAiSummary.summary}
                  </Typography>

                  {customerAiSummary.key_points?.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        Punti chiave
                      </Typography>
                      <Box component="ul" sx={{ pl: 2.5, mb: 0, mt: 0 }}>
                        {customerAiSummary.key_points.map((item, index) => (
                          <Typography component="li" variant="body2" key={`${item}-${index}`} sx={{ mb: 0.5 }}>
                            {item}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {customerAiSummary.customer_notes_summary && (
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        Focus note cliente
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {customerAiSummary.customer_notes_summary}
                      </Typography>
                    </Box>
                  )}

                  {customerAiSummary.attention_items?.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        Da tenere d'occhio
                      </Typography>
                      <Box component="ul" sx={{ pl: 2.5, mb: 0, mt: 0 }}>
                        {customerAiSummary.attention_items.map((item, index) => (
                          <Typography component="li" variant="body2" key={`${item}-${index}`} color="text.secondary" sx={{ mb: 0.5 }}>
                            {item}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}

                  <Typography variant="caption" color="text.secondary">
                    Aggiornato {customerAiSummary.generated_at ? format(new Date(customerAiSummary.generated_at), 'dd/MM/yyyy HH:mm', { locale: it }) : 'ora'}
                    {customerAiSummary.communication_style ? ` • Stile cliente: ${customerAiSummary.communication_style}` : ''}
                  </Typography>
                </Box>
              ) : (
                <Alert severity="info">
                  Nessun riassunto disponibile per questo cliente.
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Card Note Cliente */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Note
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {/* Form nuova nota */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  maxRows={4}
                  placeholder="Scrivi una nuova nota..."
                  value={newNoteText}
                  onChange={(e) => setNewNoteText(e.target.value)}
                />
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<NoteAddIcon />}
                  onClick={() => {
                    if (newNoteText.trim()) {
                      createNoteMutation.mutate(newNoteText.trim());
                    }
                  }}
                  disabled={!newNoteText.trim() || createNoteMutation.isPending}
                  sx={{ alignSelf: 'flex-start', minWidth: 'auto', whiteSpace: 'nowrap' }}
                >
                  Aggiungi
                </Button>
              </Box>

              {/* Lista note */}
              {customerNotes.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                  Nessuna nota
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {customerNotes.map((note) => (
                    <Box
                      key={note.id}
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: 'grey.50',
                        border: '1px solid',
                        borderColor: 'grey.200',
                      }}
                    >
                      {editingNoteId === note.id ? (
                        /* Modalità editing */
                        <Box>
                          <TextField
                            fullWidth
                            size="small"
                            multiline
                            minRows={2}
                            maxRows={6}
                            value={editingNoteText}
                            onChange={(e) => setEditingNoteText(e.target.value)}
                            autoFocus
                          />
                          <Box sx={{ display: 'flex', gap: 1, mt: 1, justifyContent: 'flex-end' }}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<CancelIcon />}
                              onClick={() => {
                                setEditingNoteId(null);
                                setEditingNoteText('');
                              }}
                            >
                              Annulla
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<SaveIcon />}
                              onClick={() => {
                                if (editingNoteText.trim()) {
                                  updateNoteMutation.mutate({ noteId: note.id, text: editingNoteText.trim() });
                                }
                              }}
                              disabled={!editingNoteText.trim() || updateNoteMutation.isPending}
                            >
                              Salva
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        /* Modalità visualizzazione */
                        <Box>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 0.5 }}>
                            {note.text}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                {format(new Date(note.created_at), 'dd/MM/yyyy HH:mm', { locale: it })}
                              </Typography>
                              {note.updated_at && new Date(note.updated_at).getTime() - new Date(note.created_at).getTime() > 1000 && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', ml: 1 }}>
                                  (modificata {format(new Date(note.updated_at), 'dd/MM/yyyy HH:mm', { locale: it })})
                                </Typography>
                              )}
                            </Box>
                            <Box>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setEditingNoteId(note.id);
                                  setEditingNoteText(note.text);
                                }}
                                title="Modifica nota"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setNoteToDelete(note);
                                  setDeleteNoteDialogOpen(true);
                                }}
                                title="Elimina nota"
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Dialog conferma eliminazione nota */}
          <Dialog
            open={deleteNoteDialogOpen}
            onClose={() => {
              setDeleteNoteDialogOpen(false);
              setNoteToDelete(null);
            }}
            fullScreen={isMobile}
          >
            <DialogTitle>Conferma eliminazione</DialogTitle>
            <DialogContent>
              <Typography>Sei sicuro di voler eliminare questa nota?</Typography>
              {noteToDelete && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                  "{noteToDelete.text.length > 100 ? noteToDelete.text.substring(0, 100) + '...' : noteToDelete.text}"
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => {
                setDeleteNoteDialogOpen(false);
                setNoteToDelete(null);
              }}>
                Annulla
              </Button>
              <Button
                color="error"
                variant="contained"
                onClick={() => {
                  if (noteToDelete) {
                    deleteNoteMutation.mutate(noteToDelete.id);
                  }
                }}
                disabled={deleteNoteMutation.isPending}
              >
                Elimina
              </Button>
            </DialogActions>
          </Dialog>
        </Grid>

        {/* Tabs Dettagli */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} variant={isMobile ? 'scrollable' : 'standard'} scrollButtons={isMobile ? 'auto' : false} allowScrollButtonsMobile>
                <Tab icon={<ReceiptIcon />} label="Fatture" />
                <Tab icon={<MeterIcon />} label="Contatori" />
                <Tab icon={<BuildIcon />} label="Interventi Extra" />
                <Tab icon={<ForumIcon />} label="Comunicazioni" />
                <Tab icon={<FolderIcon />} label="Documenti" />
              </Tabs>

              {/* Tab Fatture */}
              <TabPanel value={tabValue} index={0}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: isMobile ? 'stretch' : 'flex-end',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    flexDirection: { xs: 'column', sm: 'row' },
                    mb: 2,
                    gap: 1,
                  }}
                >
                  <Button
                    variant="outlined"
                    color="info"
                    startIcon={<EventRepeatIcon />}
                    fullWidth={isMobile}
                    onClick={() => {
                      setSelectedInvoiceIds([]);
                      setInstallmentRecipientType('intestatario');
                      setInstallmentRecipientData({ name: '', surname: '', cf: '', document: '' });
                      setInstallmentNumRates(3);
                      setInstallmentRates([]);
                      setInstallmentIncludeMora(true);
                      setInstallmentDialogOpen(true);
                    }}
                  >
                    Piano Rateizzazione
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<DescriptionIcon />}
                    fullWidth={isMobile}
                    onClick={() => {
                      setAccountStatementEmail(customer?.email || '');
                      setAccountStatementDialogOpen(true);
                    }}
                  >
                    Estratto Conto
                  </Button>
                </Box>

                {/* Sezione Piani di Rateizzazione esistenti */}
                {installmentPlans.length > 0 && (
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EventRepeatIcon fontSize="small" color="info" />
                      Piani di Rateizzazione ({installmentPlans.length})
                    </Typography>
                    {installmentPlans.map((plan) => {
                      const ratesPagate = (plan.rates || []).filter(r => r.paid).length;
                      const totalRates = (plan.rates || []).length;
                      const statusColors = { attivo: 'info', completato: 'success', annullato: 'default' };
                      const statusLabels = { attivo: 'Attivo', completato: 'Completato', annullato: 'Annullato' };
                      return (
                        <Box key={plan.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, p: 1, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                          <Chip
                            label={statusLabels[plan.status] || plan.status}
                            color={statusColors[plan.status] || 'default'}
                            size="small"
                            sx={{ minWidth: 80 }}
                          />
                          <Typography variant="body2" component="span" sx={{ flex: 1 }}>
                            {formatCurrency(plan.total_amount)} in {totalRates} rate
                            ({ratesPagate}/{totalRates} pagate)
                            {plan.recipient_type === 'utilizzatore' && (
                              <Chip label="Utilizzatore" size="small" variant="outlined" sx={{ ml: 1, height: 20, fontSize: '0.65rem' }} />
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {plan.created_at ? format(new Date(plan.created_at), 'dd/MM/yyyy', { locale: it }) : ''}
                          </Typography>
                          <Tooltip title="Scarica PDF">
                            <IconButton
                              size="small"
                              onClick={async () => {
                                try {
                                  const response = await customersApi.getInstallmentPlanPdf(id, plan.id);
                                  const blob = new Blob([response.data], { type: 'application/pdf' });
                                  const url = window.URL.createObjectURL(blob);
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.download = `Piano_Rateizzazione_${plan.id}_${new Date().toISOString().split('T')[0]}.pdf`;
                                  link.click();
                                  window.URL.revokeObjectURL(url);
                                } catch (err) {
                                  console.error('Errore download PDF piano:', err);
                                  alert('Errore nel download del PDF');
                                }
                              }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Invia via Email">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={async () => {
                                setInstallmentEmailPlanId(plan.id);
                                setInstallmentEmailError(null);
                                setInstallmentEmailPreview(null);
                                setInstallmentEmailLoading(true);
                                setInstallmentEmailDialogOpen(true);
                                try {
                                  const response = await customersApi.getInstallmentPlanEmailPreview(id, plan.id);
                                  const data = response.data?.data;
                                  if (!data) throw new Error('Dati preview non validi');
                                  setInstallmentEmailPreview(data);
                                  setInstallmentEmailAddress(data.to_email || customer?.email || customer?.pec || '');
                                  setInstallmentEmailSubject(data.subject || '');
                                  setInstallmentEmailBody(data.body_html || '');
                                } catch (err) {
                                  console.error('Errore caricamento preview:', err);
                                  setInstallmentEmailError(err.response?.data?.message || 'Errore nel caricamento della preview email');
                                  // Fallback: pre-compila con dati base
                                  setInstallmentEmailAddress(customer?.email || customer?.pec || '');
                                  setInstallmentEmailSubject(`Piano di Rateizzazione - ${formatCurrency(plan.total_amount)} in ${totalRates} rate`);
                                  setInstallmentEmailBody('');
                                } finally {
                                  setInstallmentEmailLoading(false);
                                }
                              }}
                            >
                              <SendIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {plan.status === 'attivo' && (
                            <>
                              <Tooltip title="Segna come completato">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={async () => {
                                    if (window.confirm('Segnare il piano come completato?')) {
                                      try {
                                        await customersApi.updateInstallmentPlan(id, plan.id, { status: 'completato' });
                                        refetchInstallmentPlans();
                                        queryClient.invalidateQueries(['customer', id]);
                                      } catch (err) {
                                        alert('Errore nell\'aggiornamento');
                                      }
                                    }
                                  }}
                                >
                                  <CheckCircleIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Annulla piano">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={async () => {
                                    if (window.confirm('Annullare il piano di rateizzazione?')) {
                                      try {
                                        await customersApi.updateInstallmentPlan(id, plan.id, { status: 'annullato' });
                                        refetchInstallmentPlans();
                                        queryClient.invalidateQueries(['customer', id]);
                                      } catch (err) {
                                        alert('Errore nell\'aggiornamento');
                                      }
                                    }
                                  }}
                                >
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          <Tooltip title="Elimina definitivamente">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={async () => {
                                if (window.confirm('Eliminare DEFINITIVAMENTE il piano di rateizzazione? Questa azione non può essere annullata.')) {
                                  try {
                                    await customersApi.deleteInstallmentPlan(id, plan.id);
                                    refetchInstallmentPlans();
                                    queryClient.invalidateQueries(['customer', id]);
                                  } catch (err) {
                                    alert('Errore nell\'eliminazione del piano');
                                  }
                                }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {isMobile ? (
                  <MobileCardList
                    rows={invoices || []}
                    emptyMessage="Nessuna fattura trovata"
                    renderCard={(invoice) => {
                      const isExpanded = expandedInvoices.includes(invoice.id);
                      const paymentsHistory = paymentsData[invoice.id];

                      return (
                        <Card variant="outlined">
                          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                  {invoice.invoice_number}/{invoice.invoice_year}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Emessa {invoice.issue_date ? format(new Date(invoice.issue_date), 'dd/MM/yyyy') : '-'}
                                  {invoice.due_date ? ` • Scad. ${format(new Date(invoice.due_date), 'dd/MM/yyyy')}` : ''}
                                </Typography>
                              </Box>
                              <Chip
                                label={
                                  invoice.status === 'paid' ? 'Pagata' :
                                  invoice.status === 'partial' ? 'Parziale' :
                                  invoice.status === 'cancelled' ? 'Annullata' :
                                  'Da pagare'
                                }
                                size="small"
                                color={
                                  invoice.status === 'paid' ? 'success' :
                                  invoice.status === 'partial' ? 'warning' :
                                  invoice.status === 'cancelled' ? 'default' :
                                  'error'
                                }
                              />
                            </Box>

                            <Grid container spacing={1.5} sx={{ mt: 0.25, mb: 1 }}>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Importo</Typography>
                                <Typography variant="body2" fontWeight="bold">{formatCurrency(invoice.total_amount)}</Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Saldo</Typography>
                                <Typography
                                  variant="body2"
                                  fontWeight="bold"
                                  color={invoice.balance > 0 ? 'error.main' : 'success.main'}
                                >
                                  {formatCurrency(invoice.balance)}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Importo pagato</Typography>
                                <Typography variant="body2">
                                  {invoice.paid_amount > 0 ? formatCurrency(invoice.paid_amount) : '-'}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Data pagamento</Typography>
                                <Typography variant="body2">
                                  {invoice.payment_date ? format(new Date(invoice.payment_date), 'dd/MM/yyyy') : '-'}
                                </Typography>
                              </Grid>
                            </Grid>

                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => navigate(`/crm/fatture/${invoice.id}`)}
                              >
                                Apri fattura
                              </Button>
                              <Button
                                size="small"
                                variant={isExpanded ? 'contained' : 'text'}
                                startIcon={isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                onClick={() => toggleInvoiceExpansion(invoice.id)}
                              >
                                {isExpanded ? 'Nascondi pagamenti' : 'Storico pagamenti'}
                              </Button>
                            </Box>

                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                                  <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <PaymentIcon fontSize="small" /> Storico Pagamenti
                                  </Typography>
                                  {paymentsHistory?.data?.summary && (
                                    <Chip
                                      size="small"
                                      label={`${(paymentsHistory.data.summary.count_advances || 0) + (paymentsHistory.data.summary.count_payments || 0)} movimenti`}
                                    />
                                  )}
                                </Box>

                                {!paymentsHistory ? (
                                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                    <CircularProgress size={24} />
                                  </Box>
                                ) : (
                                  <>
                                    {!paymentsHistory?.data?.payments || paymentsHistory.data.payments.length === 0 ? (
                                      <Typography color="text.secondary" sx={{ py: 1 }}>
                                        Nessun pagamento registrato
                                      </Typography>
                                    ) : (
                                      <Box sx={{ display: 'grid', gap: 1, mb: 1.5 }}>
                                        {paymentsHistory.data.payments.map((payment) => (
                                          <Box
                                            key={payment.id}
                                            sx={{
                                              p: 1.25,
                                              border: '1px solid',
                                              borderColor: 'divider',
                                              borderRadius: 1.5,
                                              bgcolor: 'background.paper',
                                            }}
                                          >
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                              <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="body2" fontWeight="medium">
                                                  {payment.date ? format(new Date(payment.date), 'dd/MM/yyyy') : '-'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                  {getPaymentMethodLabel(payment.payment_method)}
                                                </Typography>
                                              </Box>
                                              <Typography variant="body2" fontWeight="bold" color="success.main">
                                                {formatCurrency(payment.amount)}
                                              </Typography>
                                            </Box>
                                            <Box sx={{ mt: 0.75 }}>
                                              <Chip
                                                label={payment.source === 'foxpro' ? 'FoxPro' : 'Nuovo'}
                                                size="small"
                                                color={payment.source === 'foxpro' ? 'default' : 'success'}
                                                variant="outlined"
                                              />
                                            </Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ ...paymentNoteSx, display: 'block', mt: 0.75 }}>
                                              {payment.notes || '-'}
                                            </Typography>
                                          </Box>
                                        ))}
                                      </Box>
                                    )}

                                    {paymentsHistory?.data?.summary && (
                                      <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1.5 }}>
                                        <Grid container spacing={1.5}>
                                          <Grid item xs={12} sm={4}>
                                            <Typography variant="caption" color="text.secondary">Totale Fattura</Typography>
                                            <Typography variant="body2" fontWeight="bold">{formatCurrency(paymentsHistory.data.summary.totale_fattura)}</Typography>
                                          </Grid>
                                          <Grid item xs={12} sm={4}>
                                            <Typography variant="caption" color="text.secondary">Totale Pagato</Typography>
                                            <Typography variant="body2" fontWeight="bold" color="success.main">{formatCurrency(paymentsHistory.data.summary.totale_pagato)}</Typography>
                                          </Grid>
                                          <Grid item xs={12} sm={4}>
                                            <Typography variant="caption" color="text.secondary">Saldo Residuo</Typography>
                                            <Typography
                                              variant="body2"
                                              fontWeight="bold"
                                              color={paymentsHistory.data.summary.saldo_residuo > 0 ? 'error.main' : 'success.main'}
                                            >
                                              {formatCurrency(paymentsHistory.data.summary.saldo_residuo)}
                                            </Typography>
                                          </Grid>
                                        </Grid>
                                      </Box>
                                    )}
                                  </>
                                )}
                              </Box>
                            </Collapse>
                          </CardContent>
                        </Card>
                      );
                    }}
                  />
                ) : (
                  <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ 
                      tableLayout: 'fixed', 
                      width: '100%',
                      minWidth: 700,
                      '& .MuiTableCell-root': {
                        padding: { xs: '6px 4px', sm: '8px 12px' }
                      }
                    }}>
                      <TableHead>
                        <TableRow>
                          <TableCell width="50px" sx={{ padding: '8px 4px' }} />
                          <TableCell width="110px">N° Fattura</TableCell>
                          <TableCell width="100px">Data</TableCell>
                          <TableCell width="100px" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Scadenza</TableCell>
                          <TableCell align="right" width="100px">Importo</TableCell>
                          <TableCell width="120px" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Data Pagamento</TableCell>
                          <TableCell align="right" width="130px" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Importo Pagamento</TableCell>
                          <TableCell align="right" width="90px">Saldo</TableCell>
                          <TableCell width="100px">Stato</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(invoices || []).length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={9} align="center">
                              <Typography color="text.secondary" sx={{ py: 2 }}>
                                Nessuna fattura trovata
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          invoices.map((invoice) => {
                            const isExpanded = expandedInvoices.includes(invoice.id);
                            const paymentsHistory = paymentsData[invoice.id];
                            
                            return (
                              <React.Fragment key={invoice.id}>
                                <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
                                  <TableCell>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleInvoiceExpansion(invoice.id);
                                      }}
                                    >
                                      {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                    </IconButton>
                                  </TableCell>
                                  <TableCell
                                    onClick={() => navigate(`/crm/fatture/${invoice.id}`)}
                                    sx={{ cursor: 'pointer' }}
                                  >
                                    {invoice.invoice_number}/{invoice.invoice_year}
                                  </TableCell>
                                  <TableCell
                                    onClick={() => navigate(`/crm/fatture/${invoice.id}`)}
                                    sx={{ cursor: 'pointer' }}
                                  >
                                    {format(new Date(invoice.issue_date), 'dd/MM/yyyy')}
                                  </TableCell>
                                  <TableCell
                                    onClick={() => navigate(`/crm/fatture/${invoice.id}`)}
                                    sx={{ cursor: 'pointer', display: { xs: 'none', md: 'table-cell' } }}
                                  >
                                    {format(new Date(invoice.due_date), 'dd/MM/yyyy')}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    onClick={() => navigate(`/crm/fatture/${invoice.id}`)}
                                    sx={{ cursor: 'pointer' }}
                                  >
                                    {formatCurrency(invoice.total_amount)}
                                  </TableCell>
                                  <TableCell
                                    onClick={() => navigate(`/crm/fatture/${invoice.id}`)}
                                    sx={{ cursor: 'pointer', display: { xs: 'none', md: 'table-cell' } }}
                                  >
                                    {invoice.payment_date ? format(new Date(invoice.payment_date), 'dd/MM/yyyy') : '-'}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    onClick={() => navigate(`/crm/fatture/${invoice.id}`)}
                                    sx={{ cursor: 'pointer', display: { xs: 'none', md: 'table-cell' } }}
                                  >
                                    {invoice.paid_amount > 0 ? formatCurrency(invoice.paid_amount) : '-'}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    onClick={() => navigate(`/crm/fatture/${invoice.id}`)}
                                    sx={{ cursor: 'pointer' }}
                                  >
                                    <Typography
                                      color={invoice.balance > 0 ? 'error.main' : 'success.main'}
                                      fontWeight="medium"
                                    >
                                      {formatCurrency(invoice.balance)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell
                                    onClick={() => navigate(`/crm/fatture/${invoice.id}`)}
                                    sx={{ cursor: 'pointer' }}
                                  >
                                    <Chip
                                      label={
                                        invoice.status === 'paid' ? 'Pagata' :
                                        invoice.status === 'partial' ? 'Parziale' :
                                        invoice.status === 'cancelled' ? 'Annullata' :
                                        'Da pagare'
                                      }
                                      size="small"
                                      color={
                                        invoice.status === 'paid' ? 'success' :
                                        invoice.status === 'partial' ? 'warning' :
                                        invoice.status === 'cancelled' ? 'default' :
                                        'error'
                                      }
                                    />
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                      <Box sx={{ margin: 2 }}>
                                        <Typography variant="subtitle2" gutterBottom component="div" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                          <PaymentIcon sx={{ mr: 1 }} /> Storico Pagamenti
                                          {paymentsHistory?.data?.summary && (
                                            <Chip 
                                              size="small" 
                                              label={`${(paymentsHistory.data.summary.count_advances || 0) + (paymentsHistory.data.summary.count_payments || 0)} movimenti`}
                                              sx={{ ml: 1 }}
                                            />
                                          )}
                                        </Typography>
                                        
                                        {!paymentsHistory ? (
                                          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                            <CircularProgress size={24} />
                                          </Box>
                                        ) : (
                                          <>
                                            <Table size="small" sx={{ mb: 2 }}>
                                              <TableHead>
                                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                                  <TableCell>Data</TableCell>
                                                  <TableCell>Tipo/Causale</TableCell>
                                                  <TableCell>Note</TableCell>
                                                  <TableCell>Origine</TableCell>
                                                  <TableCell align="right">Importo</TableCell>
                                                </TableRow>
                                              </TableHead>
                                              <TableBody>
                                                {!paymentsHistory?.data?.payments || paymentsHistory.data.payments.length === 0 ? (
                                                  <TableRow>
                                                    <TableCell colSpan={5} align="center">
                                                      <Typography color="text.secondary" sx={{ py: 2 }}>Nessun pagamento registrato</Typography>
                                                    </TableCell>
                                                  </TableRow>
                                                ) : (
                                                  paymentsHistory.data.payments.map((payment) => (
                                                    <TableRow key={payment.id}>
                                                      <TableCell>{format(new Date(payment.date), 'dd/MM/yyyy')}</TableCell>
                                                      <TableCell>
                                                        {payment.payment_method ? (
                                                          <Chip 
                                                            label={getPaymentMethodLabel(payment.payment_method)}
                                                            size="small" 
                                                            variant="outlined"
                                                            color="primary"
                                                          />
                                                        ) : (
                                                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>-</Typography>
                                                        )}
                                                      </TableCell>
                                                      <TableCell>
                                                        <Typography variant="body2" sx={{ ...paymentNoteSx, minWidth: 180, maxWidth: 420 }}>
                                                          {payment.notes || '-'}
                                                        </Typography>
                                                      </TableCell>
                                                      <TableCell>
                                                        <Chip 
                                                          label={payment.source === 'foxpro' ? 'FoxPro' : 'Nuovo'}
                                                          size="small"
                                                          color={payment.source === 'foxpro' ? 'default' : 'success'}
                                                          variant="outlined"
                                                        />
                                                      </TableCell>
                                                      <TableCell align="right" sx={{ fontWeight: 'medium', color: 'success.main' }}>
                                                        {formatCurrency(payment.amount)}
                                                      </TableCell>
                                                    </TableRow>
                                                  ))
                                                )}
                                              </TableBody>
                                            </Table>
                                            
                                            {paymentsHistory?.data?.summary && (
                                              <Box sx={{ p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                                <Grid container spacing={2}>
                                                  <Grid item xs={4}>
                                                    <Typography variant="caption" color="text.secondary">Totale Fattura</Typography>
                                                    <Typography variant="body1" fontWeight="bold">{formatCurrency(paymentsHistory.data.summary.totale_fattura)}</Typography>
                                                  </Grid>
                                                  <Grid item xs={4}>
                                                    <Typography variant="caption" color="text.secondary">Totale Pagato</Typography>
                                                    <Typography variant="body1" fontWeight="bold" color="success.main">{formatCurrency(paymentsHistory.data.summary.totale_pagato)}</Typography>
                                                  </Grid>
                                                  <Grid item xs={4}>
                                                    <Typography variant="caption" color="text.secondary">Saldo Residuo</Typography>
                                                    <Typography variant="body1" fontWeight="bold" color={paymentsHistory.data.summary.saldo_residuo > 0 ? 'error.main' : 'success.main'}>
                                                      {formatCurrency(paymentsHistory.data.summary.saldo_residuo)}
                                                    </Typography>
                                                  </Grid>
                                                </Grid>
                                              </Box>
                                            )}
                                          </>
                                        )}
                                      </Box>
                                    </Collapse>
                                  </TableCell>
                                </TableRow>
                              </React.Fragment>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </TabPanel>

              {/* Tab Contatori */}
              <TabPanel value={tabValue} index={1}>
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: isMobile ? 0 : 700 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>N° Contatore</TableCell>
                        <TableCell>Indirizzo Fornitura</TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Località</TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Impianto</TableCell>
                        <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Ultima Lettura</TableCell>
                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Data Lettura</TableCell>
                        <TableCell align="right">€/Litro</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(meters || []).length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            <Typography color="text.secondary" sx={{ py: 2 }}>
                              Nessun contatore associato a questo cliente
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        meters.map((meter) => (
                          <TableRow key={meter.id} hover>
                            <TableCell>
                              <Chip 
                                label={meter.meter_number} 
                                size="small" 
                                color="primary" 
                                variant="outlined" 
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" noWrap sx={{ maxWidth: isMobile ? 120 : 'none' }}>
                                {meter.c_localita || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                              {[meter.c_citta, meter.c_provinci].filter(Boolean).join(' ') || '-'}
                            </TableCell>
                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                              {meter.plant ? (
                                <Chip 
                                  label={meter.plant.codice || meter.plant.plant_code} 
                                  size="small" 
                                  variant="outlined"
                                  onClick={() => navigate(`/crm/impianti/${meter.plant.id}`)}
                                  sx={{ cursor: 'pointer' }}
                                />
                              ) : '-'}
                            </TableCell>
                            <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                              {meter.ultima_lettura != null 
                                ? meter.ultima_lettura.toLocaleString('it-IT') 
                                : '-'} mc
                            </TableCell>
                            <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                              {meter.data_ultima_lettura && !isNaN(new Date(meter.data_ultima_lettura).getTime())
                                ? format(new Date(meter.data_ultima_lettura), 'dd/MM/yyyy', { locale: it })
                                : '-'}
                            </TableCell>
                            <TableCell align="right">
                              {meter.prezzo_litro 
                                ? `€ ${parseFloat(meter.prezzo_litro).toFixed(3)}` 
                                : '-'}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {meters && meters.length > 0 && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Riepilogo Contatori
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">
                          Totale Contatori
                        </Typography>
                        <Typography variant="h6">{meters.length}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">
                          Con Impianto
                        </Typography>
                        <Typography variant="h6">
                          {meters.filter(m => m.plant_id).length}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">
                          Consumo Totale (mc)
                        </Typography>
                        <Typography variant="h6">
                          {meters.reduce((sum, m) => sum + (parseFloat(m.consumo_reale) || 0), 0).toLocaleString('it-IT')}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">
                          Prezzo Medio €/Lt
                        </Typography>
                        <Typography variant="h6">
                          {(meters.reduce((sum, m) => sum + (parseFloat(m.prezzo_litro) || 0), 0) / meters.length).toFixed(3)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </TabPanel>

              {/* Tab Interventi Extra */}
              <TabPanel value={tabValue} index={2}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setInterventionDialogOpen(true)}
                  >
                    Nuovo Intervento
                  </Button>
                </Box>
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Data</TableCell>
                        <TableCell>Dicitura</TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Descrizione</TableCell>
                        <TableCell align="right">Importo</TableCell>
                        <TableCell>Stato</TableCell>
                        <TableCell align="center">Azioni</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(interventions || []).length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography color="text.secondary" sx={{ py: 2 }}>
                              Nessun intervento extra registrato
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        interventions.map((intervention) => (
                          <TableRow key={intervention.id} hover>
                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                              {intervention.data_intervento 
                                ? format(new Date(intervention.data_intervento), 'dd/MM/yyyy', { locale: it })
                                : '-'}
                            </TableCell>
                            <TableCell>
                              <Typography fontWeight="medium" noWrap sx={{ maxWidth: isMobile ? 120 : 'none' }}>
                                {intervention.dicitura}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                              <Typography variant="body2" color="text.secondary">
                                {intervention.descrizione || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography fontWeight="medium">
                                {formatCurrency(intervention.importo)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={intervention.fatturato ? 'Fatturato' : 'Da fatturare'}
                                size="small"
                                color={intervention.fatturato ? 'success' : 'warning'}
                              />
                            </TableCell>
                            <TableCell align="center">
                              {!intervention.fatturato && (
                                <Tooltip title="Elimina intervento">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => {
                                      if (window.confirm('Sei sicuro di voler eliminare questo intervento?')) {
                                        deleteInterventionMutation.mutate(intervention.id);
                                      }
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Riepilogo interventi da fatturare */}
                {interventions && interventions.filter(i => !i.fatturato).length > 0 && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Da Fatturare
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography>
                        {interventions.filter(i => !i.fatturato).length} interventi in attesa
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {formatCurrency(
                          interventions
                            .filter(i => !i.fatturato)
                            .reduce((sum, i) => sum + parseFloat(i.importo || 0), 0)
                        )}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </TabPanel>

              {/* Tab Comunicazioni */}
              <TabPanel value={tabValue} index={3}>
                {communicationsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : communications.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">
                      Nessuna comunicazione con questo cliente
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {/* Legenda */}
                    <Box sx={{ display: 'flex', gap: 3, mb: 2, px: 1, flexWrap: 'wrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <SendIcon fontSize="small" color="primary" />
                        <Typography variant="body2" color="primary.main">Email inviate</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <InboxIcon fontSize="small" color="success" />
                        <Typography variant="body2" color="success.main">Email ricevute</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <WhatsAppIcon fontSize="small" sx={{ color: '#25D366' }} />
                        <Typography variant="body2" sx={{ color: '#25D366' }}>WhatsApp</Typography>
                      </Box>
                    </Box>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell width={50}>Canale</TableCell>
                          <TableCell width={60}>Tipo</TableCell>
                          <TableCell>Data</TableCell>
                          <TableCell>Oggetto</TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Anteprima</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {communications.map((msg) => {
                          const isWhatsapp = msg.channel === 'whatsapp';
                          const isSent = msg.direction === 'out'
                            || msg.folder === 'Sent'
                            || msg.folder === 'INBOX.Sent'
                            || (msg.folder || '').toLowerCase().includes('sent');

                          // Colori riga
                          let bgColor = 'rgba(46, 125, 50, 0.04)'; // email ricevuta
                          let borderColor = '#2e7d32';
                          let hoverColor = 'rgba(46, 125, 50, 0.12)';
                          if (isWhatsapp) {
                            bgColor = 'rgba(37, 211, 102, 0.06)';
                            borderColor = '#25D366';
                            hoverColor = 'rgba(37, 211, 102, 0.15)';
                          } else if (isSent) {
                            bgColor = 'rgba(25, 118, 210, 0.04)';
                            borderColor = '#1976d2';
                            hoverColor = 'rgba(25, 118, 210, 0.12)';
                          }

                          const handleClick = () => {
                            if (!isWhatsapp) {
                              navigate(`/crm/email/${msg.id}`);
                            }
                            // Per WhatsApp non navighiamo (per ora), ma potremmo aprire una pagina di dettaglio in futuro
                          };

                          const preview = msg.body_text || msg.body || '';

                          return (
                            <TableRow
                              key={`${msg.channel || 'email'}-${msg.id}`}
                              hover
                              sx={{
                                cursor: isWhatsapp ? 'default' : 'pointer',
                                backgroundColor: bgColor,
                                borderLeft: `3px solid ${borderColor}`,
                                '&:hover': { backgroundColor: hoverColor }
                              }}
                              onClick={handleClick}
                            >
                              <TableCell>
                                {isWhatsapp ? (
                                  <Tooltip title={isSent ? 'WhatsApp inviato' : 'WhatsApp ricevuto'}>
                                    <WhatsAppIcon fontSize="small" sx={{ color: '#25D366' }} />
                                  </Tooltip>
                                ) : (
                                  <Tooltip title="Email">
                                    <EmailIcon fontSize="small" color="action" />
                                  </Tooltip>
                                )}
                              </TableCell>
                              <TableCell>
                                <Tooltip title={isSent ? 'Inviato' : 'Ricevuto'}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {isSent ? (
                                      <SendIcon fontSize="small" sx={{ color: isWhatsapp ? '#25D366' : '#1976d2' }} />
                                    ) : (
                                      <InboxIcon fontSize="small" sx={{ color: isWhatsapp ? '#25D366' : '#2e7d32' }} />
                                    )}
                                  </Box>
                                </Tooltip>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{ color: isWhatsapp ? '#25D366' : (isSent ? 'primary.main' : 'success.main') }}
                                >
                                  {msg.date_sent
                                    ? format(new Date(msg.date_sent), 'dd/MM/yyyy HH:mm', { locale: it })
                                    : '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  fontWeight={msg.is_read ? 'normal' : 'bold'}
                                  sx={{ maxWidth: isMobile ? 160 : 300 }}
                                  noWrap
                                >
                                  {isWhatsapp
                                    ? (msg.subject || `WhatsApp ${isSent ? 'a' : 'da'} ${msg.phone_number || ''}`)
                                    : (msg.subject || '(Nessun oggetto)')}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ maxWidth: 400 }}
                                  noWrap
                                >
                                  {preview.substring(0, 100)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  </>
                )}
              </TabPanel>

              {/* Tab Documenti */}
              <TabPanel value={tabValue} index={4}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => setUploadDialogOpen(true)}
                  >
                    Carica Documento
                  </Button>
                </Box>

                {documentsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : documents.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <FolderIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography color="text.secondary">
                      Nessun documento caricato per questo cliente
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Clicca su "Carica Documento" per aggiungere PDF, immagini, Word o ZIP
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell width={50}>Tipo</TableCell>
                          <TableCell>Nome File</TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Descrizione</TableCell>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Dimensione</TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Data Caricamento</TableCell>
                          <TableCell align="center">Azioni</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {documents.map((doc) => {
                          const isPdf = doc.mimetype === 'application/pdf';
                          const isImage = doc.mimetype?.startsWith('image/');
                          const isWord = doc.mimetype?.includes('word') || doc.filename?.endsWith('.doc') || doc.filename?.endsWith('.docx');
                          const isZip = doc.mimetype?.includes('zip') || doc.filename?.endsWith('.zip');
                          const sizeKB = Math.round(doc.size / 1024);
                          const sizeDisplay = sizeKB > 1024 
                            ? `${(sizeKB / 1024).toFixed(1)} MB` 
                            : `${sizeKB} KB`;
                          
                          const getFileIcon = () => {
                            if (isPdf) return <PdfIcon color="error" />;
                            if (isWord) return <WordIcon color="primary" />;
                            if (isZip) return <ZipIcon color="warning" />;
                            return <ImageIcon color="success" />;
                          };
                          const getFileTooltip = () => {
                            if (isPdf) return 'Documento PDF';
                            if (isWord) return 'Documento Word';
                            if (isZip) return 'Archivio ZIP';
                            return 'Immagine';
                          };
                          
                          return (
                            <TableRow key={doc.id} id={'customer-document-'+doc.id} tabIndex={-1} selected={sectionParams.get('record')===String(doc.id)} hover>
                              <TableCell>
                                <Tooltip title={getFileTooltip()}>
                                  {getFileIcon()}
                                </Tooltip>
                              </TableCell>
                              <TableCell>
                                <Typography fontWeight="medium" noWrap sx={{ maxWidth: isMobile ? 120 : 200 }}>
                                  {doc.filename}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                                  {doc.description || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                <Typography variant="body2">
                                  {sizeDisplay}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                <Typography variant="body2">
                                  {doc.uploaded_at 
                                    ? format(new Date(doc.uploaded_at), 'dd/MM/yyyy HH:mm', { locale: it })
                                    : '-'}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip title="Visualizza">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleViewDocument(doc)}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Scarica">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleDownloadDocument(doc)}
                                  >
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Elimina">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => {
                                      if (window.confirm('Sei sicuro di voler eliminare questo documento?')) {
                                        deleteDocumentMutation.mutate(doc.id);
                                      }
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {/* Riepilogo documenti */}
                {documents && documents.length > 0 && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Riepilogo Documenti
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">
                          Totale Documenti
                        </Typography>
                        <Typography variant="h6">{documents.length}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">
                          PDF
                        </Typography>
                        <Typography variant="h6">
                          {documents.filter(d => d.mimetype === 'application/pdf').length}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">
                          Immagini
                        </Typography>
                        <Typography variant="h6">
                          {documents.filter(d => d.mimetype.startsWith('image/')).length}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </TabPanel>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog Upload Documento */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Carica Documento</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Alert severity="info">
              Sono accettati: PDF, JPEG, PNG, Word (.doc, .docx) e ZIP (max 10 MB)
            </Alert>
            
            <Box
              sx={{
                border: '2px dashed',
                borderColor: selectedFile ? 'primary.main' : 'grey.300',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                bgcolor: selectedFile ? 'primary.50' : 'grey.50',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'primary.50'
                }
              }}
              onClick={() => document.getElementById('file-upload-input').click()}
            >
              <input
                id="file-upload-input"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.zip"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    if (file.size > 10 * 1024 * 1024) {
                      alert('Il file è troppo grande. Dimensione massima: 10 MB');
                      return;
                    }
                    setSelectedFile(file);
                  }
                }}
              />
              {selectedFile ? (
                <Box>
                  {selectedFile.type === 'application/pdf' ? (
                    <PdfIcon sx={{ fontSize: 48, color: 'error.main', mb: 1 }} />
                  ) : selectedFile.type?.includes('word') || selectedFile.name?.endsWith('.doc') || selectedFile.name?.endsWith('.docx') ? (
                    <WordIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                  ) : selectedFile.type?.includes('zip') || selectedFile.name?.endsWith('.zip') ? (
                    <ZipIcon sx={{ fontSize: 48, color: 'warning.main', mb: 1 }} />
                  ) : (
                    <ImageIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                  )}
                  <Typography fontWeight="medium">{selectedFile.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {Math.round(selectedFile.size / 1024)} KB
                  </Typography>
                  <Button
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                    }}
                  >
                    Rimuovi
                  </Button>
                </Box>
              ) : (
                <Box>
                  <CloudUploadIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                  <Typography color="text.secondary">
                    Clicca per selezionare un file
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    oppure trascina qui
                  </Typography>
                </Box>
              )}
            </Box>

            <TextField
              label="Descrizione (opzionale)"
              value={fileDescription}
              onChange={(e) => setFileDescription(e.target.value)}
              placeholder="es. Contratto di fornitura, Documento identità, ecc."
              multiline
              rows={2}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setUploadDialogOpen(false);
            setSelectedFile(null);
            setFileDescription('');
          }}>
            Annulla
          </Button>
          <Button 
            variant="contained" 
            onClick={handleUploadDocument}
            disabled={!selectedFile || uploading}
            startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
          >
            {uploading ? 'Caricamento...' : 'Carica'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Nuovo Intervento */}
      <Dialog open={interventionDialogOpen} onClose={() => setInterventionDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Nuovo Intervento Extra</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {metersCount === 0 && (
              <Alert severity="warning">
                Nessun contatore associato a questo cliente: non è possibile creare interventi extra.
              </Alert>
            )}

            {requiresMeterSelection && (
              <FormControl fullWidth required>
                <InputLabel>Contatore</InputLabel>
                <Select
                  label="Contatore"
                  value={interventionForm.meter_id}
                  onChange={(e) => setInterventionForm({ ...interventionForm, meter_id: e.target.value })}
                >
                  <MenuItem value="">
                    <em>Seleziona...</em>
                  </MenuItem>
                  {(meters || []).map((m) => (
                    <MenuItem key={m.id} value={String(m.id)}>
                      {m.meter_number ? `Contatore ${m.meter_number}` : `Contatore #${m.id}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              label="Dicitura"
              value={interventionForm.dicitura}
              onChange={(e) => setInterventionForm({...interventionForm, dicitura: e.target.value})}
              placeholder="es. Chiusura contatore"
              required
              fullWidth
            />
            <TextField
              label="Descrizione"
              value={interventionForm.descrizione}
              onChange={(e) => setInterventionForm({...interventionForm, descrizione: e.target.value})}
              placeholder="es. Chiusura contatore per morosità del 10/12/2025"
              multiline
              rows={2}
              fullWidth
            />
            <TextField
              label="Importo (€)"
              type="number"
              value={interventionForm.importo}
              onChange={(e) => setInterventionForm({...interventionForm, importo: e.target.value})}
              inputProps={{ min: 0, step: 0.01 }}
              required
              fullWidth
            />
            <TextField
              label="Data Intervento"
              type="date"
              value={interventionForm.data_intervento}
              onChange={(e) => setInterventionForm({...interventionForm, data_intervento: e.target.value})}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Note"
              value={interventionForm.note}
              onChange={(e) => setInterventionForm({...interventionForm, note: e.target.value})}
              multiline
              rows={2}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInterventionDialogOpen(false)}>Annulla</Button>
          <Button 
            variant="contained" 
            onClick={() => {
              const payload = {
                ...interventionForm,
                meter_id: interventionForm.meter_id ? Number(interventionForm.meter_id) : undefined,
              };
              createInterventionMutation.mutate(payload);
            }}
            disabled={!canSaveIntervention || metersCount === 0 || createInterventionMutation.isLoading}
          >
            {createInterventionMutation.isLoading ? 'Salvataggio...' : 'Salva Intervento'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Estratto Conto */}
      <Dialog 
        open={accountStatementDialogOpen} 
        onClose={() => !accountStatementLoading && setAccountStatementDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DescriptionIcon color="primary" />
            Estratto Conto
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Seleziona la fattura da cui iniziare l'estratto conto per <strong>{formatCustomerName(customer)}</strong>.
              Verranno incluse tutte le fatture dalla data selezionata in poi.
            </Typography>
            
            {/* Tabella fatture */}
            <TableContainer sx={{ maxHeight: 350, mb: 2 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ fontWeight: 700, bgcolor: 'primary.main', color: 'white' }}>Sel.</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'primary.main', color: 'white' }}>N° Fattura</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'primary.main', color: 'white' }}>Data</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'primary.main', color: 'white' }}>Importo</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'primary.main', color: 'white' }}>Pagato</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, bgcolor: 'primary.main', color: 'white' }}>Stato</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Opzione "Tutte le fatture" */}
                  <TableRow 
                    hover 
                    selected={selectedFromInvoiceId === null}
                    onClick={() => setSelectedFromInvoiceId(null)}
                    sx={{ cursor: 'pointer', bgcolor: selectedFromInvoiceId === null ? 'action.selected' : 'inherit' }}
                  >
                    <TableCell padding="checkbox">
                      <Radio 
                        checked={selectedFromInvoiceId === null} 
                        onChange={() => setSelectedFromInvoiceId(null)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" fontWeight={600} color="primary">
                        📋 Tutte le fatture (dall'inizio)
                      </Typography>
                    </TableCell>
                  </TableRow>
                  
                  {/* Lista fatture ordinate per data */}
                  {(invoices || [])
                    .slice()
                    .sort((a, b) => new Date(a.f_emission) - new Date(b.f_emission))
                    .map((inv) => {
                      const importo = parseFloat(inv.f_totale || 0);
                      const pagato = parseFloat(inv.f_paga_imp || 0);
                      const saldo = importo - pagato;
                      const isCreditNote = importo < 0;
                      // Per le NC: pagata quando saldo >= 0 (rimborsata)
                      // Per le fatture: pagata quando saldo <= 0.01
                      const isPaid = isCreditNote ? saldo >= -0.01 : saldo <= 0.01;
                      const isSelected = selectedFromInvoiceId === inv.id;
                      
                      // Determina label e colore dello stato
                      let statusLabel = '';
                      let statusColor = 'default';
                      if (isCreditNote) {
                        statusLabel = isPaid ? 'Rimborsata' : 'NC Aperta';
                        statusColor = isPaid ? 'success' : 'info';
                      } else {
                        statusLabel = isPaid ? 'Pagata' : Math.abs(saldo) < Math.abs(importo) - 0.01 ? 'Parziale' : 'Da pagare';
                        statusColor = isPaid ? 'success' : Math.abs(saldo) < Math.abs(importo) - 0.01 ? 'warning' : 'error';
                      }
                      
                      return (
                        <TableRow 
                          key={inv.id} 
                          hover 
                          selected={isSelected}
                          onClick={() => setSelectedFromInvoiceId(inv.id)}
                          sx={{ 
                            cursor: 'pointer', 
                            bgcolor: isSelected ? 'action.selected' : isCreditNote ? 'info.50' : 'inherit' 
                          }}
                        >
                          <TableCell padding="checkbox">
                            <Radio 
                              checked={isSelected} 
                              onChange={() => setSelectedFromInvoiceId(inv.id)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body2" fontWeight={500}>
                                {formatNuf(inv.sezionale, inv.nuf)}/{inv.f_emission ? new Date(inv.f_emission).getFullYear() : ''}
                              </Typography>
                              {isCreditNote && (
                                <Chip label="NC" size="small" color="info" sx={{ height: 18, fontSize: '0.65rem' }} />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {inv.f_emission ? format(new Date(inv.f_emission), 'dd/MM/yyyy', { locale: it }) : '-'}
                          </TableCell>
                          <TableCell align="right" sx={{ color: isCreditNote ? 'info.main' : 'inherit' }}>
                            {formatCurrency(importo)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(pagato)}
                          </TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={statusLabel}
                              color={statusColor}
                              size="small"
                              sx={{ minWidth: 80 }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            
            {selectedFromInvoiceId && (
              <Alert severity="info" sx={{ mb: 2 }}>
                L'estratto conto includerà solo le fatture dalla fattura selezionata in poi.
              </Alert>
            )}
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Invia per Email</Typography>
            <TextField
              label="Indirizzo Email"
              value={accountStatementEmail}
              onChange={(e) => setAccountStatementEmail(e.target.value)}
              fullWidth
              size="small"
              placeholder="email@esempio.it"
              helperText="L'estratto conto verrà inviato in PDF come allegato"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => setAccountStatementDialogOpen(false)}
            disabled={accountStatementLoading}
          >
            Annulla
          </Button>
          <Button
            variant="outlined"
            startIcon={accountStatementLoading ? <CircularProgress size={16} /> : <DownloadIcon />}
            disabled={accountStatementLoading}
            onClick={async () => {
              setAccountStatementLoading(true);
              try {
                const response = await customersApi.getAccountStatementPdf(id, selectedFromInvoiceId);
                const blob = new Blob([response.data], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `Estratto_Conto_${(formatCustomerName(customer, 'cliente')).replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
                link.click();
                window.URL.revokeObjectURL(url);
                setAccountStatementDialogOpen(false);
              } catch (error) {
                console.error('Errore download estratto conto:', error);
                alert('Errore nella generazione dell\'estratto conto');
              } finally {
                setAccountStatementLoading(false);
              }
            }}
          >
            Scarica PDF
          </Button>
          <Button
            variant="contained"
            startIcon={accountStatementLoading ? <CircularProgress size={16} color="inherit" /> : <EmailIcon />}
            disabled={accountStatementLoading || !accountStatementEmail}
            onClick={async () => {
              setAccountStatementLoading(true);
              try {
                await customersApi.sendAccountStatement(id, accountStatementEmail, selectedFromInvoiceId);
                alert(`Estratto conto inviato a ${accountStatementEmail}`);
                setAccountStatementDialogOpen(false);
              } catch (error) {
                console.error('Errore invio estratto conto:', error);
                alert('Errore nell\'invio dell\'estratto conto');
              } finally {
                setAccountStatementLoading(false);
              }
            }}
          >
            Invia Email
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Piano di Rateizzazione */}
      <Dialog
        open={installmentDialogOpen}
        onClose={() => !installmentLoading && setInstallmentDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EventRepeatIcon color="info" />
            Piano di Rateizzazione
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {/* SEZIONE 1: Selezione fatture */}
            <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
              1. Seleziona le fatture da rateizzare
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Seleziona le fatture non pagate di <strong>{formatCustomerName(customer)}</strong> da includere nel piano di rateizzazione.
            </Typography>
            <TableContainer sx={{ maxHeight: 250, mb: 2 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ fontWeight: 700, bgcolor: 'info.main', color: 'white' }}>
                      <MuiCheckbox
                        size="small"
                        sx={{ color: 'white', '&.Mui-checked': { color: 'white' } }}
                        checked={
                          (invoices || []).filter(inv => {
                            const saldo = parseFloat(inv.f_totale || 0) - parseFloat(inv.f_paga_imp || 0);
                            return saldo > 0.01 && parseFloat(inv.f_totale || 0) > 0;
                          }).length > 0 &&
                          selectedInvoiceIds.length === (invoices || []).filter(inv => {
                            const saldo = parseFloat(inv.f_totale || 0) - parseFloat(inv.f_paga_imp || 0);
                            return saldo > 0.01 && parseFloat(inv.f_totale || 0) > 0;
                          }).length
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            const unpaidIds = (invoices || []).filter(inv => {
                              const saldo = parseFloat(inv.f_totale || 0) - parseFloat(inv.f_paga_imp || 0);
                              return saldo > 0.01 && parseFloat(inv.f_totale || 0) > 0;
                            }).map(inv => inv.id);
                            setSelectedInvoiceIds(unpaidIds);
                            const total = (invoices || []).filter(inv => unpaidIds.includes(inv.id)).reduce((s, inv) => s + Math.max(parseFloat(inv.f_totale || 0) - parseFloat(inv.f_paga_imp || 0), 0) + (installmentIncludeMora ? calcolaMora(inv) : 0), 0);
                            handleUpdateRates(installmentNumRates, total);
                          } else {
                            setSelectedInvoiceIds([]);
                            setInstallmentRates([]);
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'info.main', color: 'white' }}>N° Fattura</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'info.main', color: 'white' }}>Data</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'info.main', color: 'white' }}>Scadenza</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'info.main', color: 'white' }}>Importo</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'info.main', color: 'white' }}>Pagato</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'info.main', color: 'white' }}>Saldo</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'info.main', color: 'white' }}>Mora</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'info.main', color: 'white' }}>Totale</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(invoices || [])
                    .filter(inv => {
                      const saldo = parseFloat(inv.f_totale || 0) - parseFloat(inv.f_paga_imp || 0);
                      return saldo > 0.01 && parseFloat(inv.f_totale || 0) > 0;
                    })
                    .sort((a, b) => new Date(a.f_emission) - new Date(b.f_emission))
                    .map((inv) => {
                      const importo = parseFloat(inv.f_totale || 0);
                      const pagato = parseFloat(inv.f_paga_imp || 0);
                      const saldo = importo - pagato;
                      const moraCalcolata = calcolaMora(inv);
                      const mora = installmentIncludeMora ? moraCalcolata : 0;
                      const totaleDovuto = saldo + mora;
                      const isChecked = selectedInvoiceIds.includes(inv.id);
                      return (
                        <TableRow
                          key={inv.id}
                          hover
                          selected={isChecked}
                          onClick={() => {
                            const newIds = isChecked
                              ? selectedInvoiceIds.filter(x => x !== inv.id)
                              : [...selectedInvoiceIds, inv.id];
                            setSelectedInvoiceIds(newIds);
                            const total = (invoices || []).filter(i => newIds.includes(i.id)).reduce((s, i) => s + Math.max(parseFloat(i.f_totale || 0) - parseFloat(i.f_paga_imp || 0), 0) + (installmentIncludeMora ? calcolaMora(i) : 0), 0);
                            handleUpdateRates(installmentNumRates, total);
                          }}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell padding="checkbox">
                            <MuiCheckbox checked={isChecked} size="small" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {formatNuf(inv.sezionale, inv.nuf)}/{inv.f_emission ? new Date(inv.f_emission).getFullYear() : ''}
                            </Typography>
                          </TableCell>
                          <TableCell>{inv.f_emission ? format(new Date(inv.f_emission), 'dd/MM/yyyy', { locale: it }) : '-'}</TableCell>
                          <TableCell>{inv.f_ult_gior ? format(new Date(inv.f_ult_gior), 'dd/MM/yyyy', { locale: it }) : '-'}</TableCell>
                          <TableCell align="right">{formatCurrency(importo)}</TableCell>
                          <TableCell align="right">{formatCurrency(pagato)}</TableCell>
                          <TableCell align="right">{formatCurrency(saldo)}</TableCell>
                          <TableCell align="right" sx={{ color: mora > 0 ? 'warning.dark' : 'text.secondary' }}>{mora > 0 ? formatCurrency(mora) : '-'}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: 'error.main' }}>{formatCurrency(totaleDovuto)}</TableCell>
                        </TableRow>
                      );
                    })}
                  {(invoices || []).filter(inv => {
                    const saldo = parseFloat(inv.f_totale || 0) - parseFloat(inv.f_paga_imp || 0);
                    return saldo > 0.01 && parseFloat(inv.f_totale || 0) > 0;
                  }).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                          Nessuna fattura con saldo residuo
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {/* Toggle mora */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={installmentIncludeMora}
                    onChange={(e) => {
                      setInstallmentIncludeMora(e.target.checked);
                      // Ricalcola rate con il nuovo totale
                      const newTotal = (invoices || [])
                        .filter(inv => selectedInvoiceIds.includes(inv.id))
                        .reduce((s, inv) => {
                          const saldo = Math.max(parseFloat(inv.f_totale || 0) - parseFloat(inv.f_paga_imp || 0), 0);
                          return s + saldo + (e.target.checked ? calcolaMora(inv) : 0);
                        }, 0);
                      handleUpdateRates(installmentNumRates, newTotal);
                    }}
                    size="small"
                    color="warning"
                  />
                }
                label="Includi interessi di mora nel conteggio"
              />
            </Box>

            {selectedInvoiceIds.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>{selectedInvoiceIds.length}</strong> fatture selezionate — Totale da rateizzare: <strong>{formatCurrency(selectedInvoicesTotal)}</strong>
                {installmentIncludeMora && selectedInvoicesMora > 0 && (
                  <span> (di cui interessi di mora: <strong>{formatCurrency(selectedInvoicesMora)}</strong>)</span>
                )}
                {!installmentIncludeMora && (
                  <span> (mora esclusa)</span>
                )}
              </Alert>
            )}

            <Divider sx={{ my: 2 }} />

            {/* SEZIONE 2: Destinatario */}
            <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
              2. Destinatario della lettera
            </Typography>
            <RadioGroup
              row
              value={installmentRecipientType}
              onChange={(e) => setInstallmentRecipientType(e.target.value)}
            >
              <FormControlLabel value="intestatario" control={<Radio size="small" />} label="Intestatario utenza" />
              <FormControlLabel value="utilizzatore" control={<Radio size="small" />} label="Reale utilizzatore" />
            </RadioGroup>

            {installmentRecipientType === 'utilizzatore' && (
              <Box sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Dati anagrafici del reale utilizzatore:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="Nome"
                      value={installmentRecipientData.name}
                      onChange={(e) => setInstallmentRecipientData(prev => ({ ...prev, name: e.target.value }))}
                      fullWidth
                      size="small"
                      required
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Cognome"
                      value={installmentRecipientData.surname}
                      onChange={(e) => setInstallmentRecipientData(prev => ({ ...prev, surname: e.target.value }))}
                      fullWidth
                      size="small"
                      required
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Codice Fiscale"
                      value={installmentRecipientData.cf}
                      onChange={(e) => setInstallmentRecipientData(prev => ({ ...prev, cf: e.target.value.toUpperCase() }))}
                      fullWidth
                      size="small"
                      required
                      inputProps={{ maxLength: 16 }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Numero Documento"
                      value={installmentRecipientData.document}
                      onChange={(e) => setInstallmentRecipientData(prev => ({ ...prev, document: e.target.value }))}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* SEZIONE 3: Definizione rate */}
            <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
              3. Definisci il piano di rateizzazione
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <TextField
                label="Numero Rate"
                type="number"
                value={installmentNumRates}
                onChange={(e) => {
                  const num = Math.max(1, Math.min(24, parseInt(e.target.value) || 1));
                  setInstallmentNumRates(num);
                  handleUpdateRates(num, selectedInvoicesTotal);
                }}
                size="small"
                inputProps={{ min: 1, max: 24 }}
                sx={{ width: 130 }}
              />
              <Typography variant="body2" color="text.secondary">
                Importo per rata: <strong>{formatCurrency(selectedInvoicesTotal / (installmentNumRates || 1))}</strong>
              </Typography>
            </Box>

            {installmentRates.length > 0 && (
              <TableContainer sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'info.light', color: 'white', width: 80 }}>N° Rata</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'info.light', color: 'white' }}>Importo (€)</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'info.light', color: 'white' }}>Scadenza</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {installmentRates.map((rate, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>{rate.rate_number}</TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            value={rate.amount}
                            onChange={(e) => {
                              const newRates = [...installmentRates];
                              newRates[idx] = { ...newRates[idx], amount: parseFloat(e.target.value) || 0 };
                              setInstallmentRates(newRates);
                            }}
                            size="small"
                            inputProps={{ step: '0.01', min: 0 }}
                            sx={{ width: 140 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="date"
                            value={rate.due_date}
                            onChange={(e) => {
                              const newRates = [...installmentRates];
                              newRates[idx] = { ...newRates[idx], due_date: e.target.value };
                              setInstallmentRates(newRates);
                            }}
                            size="small"
                            sx={{ width: 170 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {installmentRates.length > 0 && (() => {
              const sumRates = installmentRates.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
              const diff = Math.round((selectedInvoicesTotal - sumRates) * 100) / 100;
              return Math.abs(diff) > 0.01 ? (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  La somma delle rate ({formatCurrency(sumRates)}) non corrisponde al totale da rateizzare ({formatCurrency(selectedInvoicesTotal)}). Differenza: {formatCurrency(Math.abs(diff))}
                </Alert>
              ) : (
                <Alert severity="success" sx={{ mb: 1 }}>
                  ✅ La somma delle rate corrisponde al totale da rateizzare: {formatCurrency(sumRates)}
                </Alert>
              );
            })()}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setInstallmentDialogOpen(false)}
            disabled={installmentLoading}
          >
            Annulla
          </Button>
          <Button
            variant="contained"
            color="info"
            startIcon={installmentLoading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
            disabled={
              installmentLoading ||
              selectedInvoiceIds.length === 0 ||
              installmentRates.length === 0 ||
              (installmentRecipientType === 'utilizzatore' && (!installmentRecipientData.name || !installmentRecipientData.surname || !installmentRecipientData.cf))
            }
            onClick={async () => {
              setInstallmentLoading(true);
              try {
                const response = await customersApi.createInstallmentPlan(id, {
                  invoiceIds: selectedInvoiceIds,
                  recipientType: installmentRecipientType,
                  recipientName: installmentRecipientData.name,
                  recipientSurname: installmentRecipientData.surname,
                  recipientCf: installmentRecipientData.cf,
                  recipientDocument: installmentRecipientData.document,
                  rates: installmentRates,
                  includeMora: installmentIncludeMora,
                  notes: ''
                });
                const blob = new Blob([response.data], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `Piano_Rateizzazione_${(formatCustomerName(customer, 'cliente')).replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
                link.click();
                window.URL.revokeObjectURL(url);
                setInstallmentDialogOpen(false);
                // Aggiorna dati
                refetchInstallmentPlans();
                queryClient.invalidateQueries(['customer', id]);
              } catch (error) {
                console.error('Errore creazione piano rateizzazione:', error);
                alert('Errore nella creazione del piano di rateizzazione');
              } finally {
                setInstallmentLoading(false);
              }
            }}
          >
            Genera PDF e Crea Piano
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Contratto GPL */}
      <Dialog open={contractDialogOpen} onClose={() => setContractDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          {contractAction === 'download' ? '📄 Scarica Contratto GPL' : '📧 Invia Contratto via Email'}
        </DialogTitle>
        <DialogContent>
          {/* Selezione contatore se ce ne sono più di uno */}
          {meters && meters.length > 1 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Il cliente ha più contatori. Seleziona quello per cui generare il contratto:
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>Contatore</InputLabel>
                <Select
                  value={selectedMeterId || ''}
                  onChange={(e) => setSelectedMeterId(e.target.value)}
                  label="Contatore"
                >
                  {meters.map((meter) => (
                    <MenuItem key={meter.id} value={meter.id}>
                      {meter.meter_number} - {meter.localita || meter.citta || 'N/D'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Info contatore selezionato */}
          {meters && meters.length === 1 && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Contatore: <strong>{meters[0].meter_number}</strong>
                {meters[0].localita && ` - ${meters[0].localita}`}
              </Typography>
            </Box>
          )}

          {/* Nessun contatore */}
          {(!meters || meters.length === 0) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Nessun contatore associato. Il contratto sarà generato solo con i dati del cliente.
            </Alert>
          )}

          {/* Campo email per invio */}
          {contractAction === 'send' && (
            <TextField
              label="Indirizzo Email"
              type="email"
              value={contractEmail}
              onChange={(e) => setContractEmail(e.target.value)}
              fullWidth
              sx={{ mt: 2 }}
              helperText="L'email a cui inviare il contratto"
            />
          )}

          {contractAction === 'download' && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Il contratto verrà generato in formato PDF con tutti i dati del cliente e le condizioni contrattuali.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContractDialogOpen(false)} disabled={contractLoading}>
            Annulla
          </Button>
          {contractAction === 'download' ? (
            <Button
              variant="contained"
              startIcon={contractLoading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
              disabled={contractLoading || (meters && meters.length > 1 && !selectedMeterId)}
              onClick={handleDownloadContract}
            >
              Scarica PDF
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={contractLoading ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              disabled={contractLoading || !contractEmail || (meters && meters.length > 1 && !selectedMeterId)}
              onClick={handleSendContract}
            >
              Invia Email
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog Parametri Contratto */}
      <Dialog open={contractParamsDialogOpen} onClose={() => setContractParamsDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BuildIcon color="secondary" />
            Parametri Contratto GPL
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Inserisci i parametri contrattuali che verranno utilizzati per la generazione del contratto PDF.
          </Typography>

          {/* Selezione contatore se ce ne sono più di uno */}
          {meters && meters.length > 1 && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Seleziona Contatore</InputLabel>
              <Select
                value={contractParamsMeterId || ''}
                onChange={(e) => handleSelectMeterForParams(e.target.value)}
                label="Seleziona Contatore"
              >
                {meters.map((meter) => (
                  <MenuItem key={meter.id} value={meter.id}>
                    {meter.meter_number} - {meter.localita || meter.citta || 'N/D'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {meters && meters.length === 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Nessun contatore associato al cliente. I parametri contratto vengono salvati sul contatore.
            </Alert>
          )}

          {(contractParamsMeterId || meters?.length === 1) && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                  Condizioni Economiche
                </Typography>
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  label="Prezzo al Litro (€)"
                  type="number"
                  inputProps={{ step: '0.0001', min: 0 }}
                  value={contractParamsForm.prezzo_litro}
                  onChange={(e) => setContractParamsForm(prev => ({ ...prev, prezzo_litro: e.target.value }))}
                  fullWidth
                  size="small"
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  label="Aliquota IVA (%)"
                  type="number"
                  inputProps={{ step: '1', min: 0, max: 100 }}
                  value={contractParamsForm.aliquota_iva}
                  onChange={(e) => setContractParamsForm(prev => ({ ...prev, aliquota_iva: e.target.value }))}
                  fullWidth
                  size="small"
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  label="Quota Fissa Mensile (€)"
                  type="number"
                  inputProps={{ step: '0.01', min: 0 }}
                  value={contractParamsForm.quota_fissa_mensile}
                  onChange={(e) => setContractParamsForm(prev => ({ ...prev, quota_fissa_mensile: e.target.value }))}
                  fullWidth
                  size="small"
                  helperText="0 = Non Dovuta"
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  label="Deposito Cauzionale (€)"
                  type="number"
                  inputProps={{ step: '0.01', min: 0 }}
                  value={contractParamsForm.deposito_cauzionale}
                  onChange={(e) => setContractParamsForm(prev => ({ ...prev, deposito_cauzionale: e.target.value }))}
                  fullWidth
                  size="small"
                  helperText="0 = Non Dovuto"
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  label="Spese Allaccio (€)"
                  type="number"
                  inputProps={{ step: '0.01', min: 0 }}
                  value={contractParamsForm.spese_allaccio}
                  onChange={(e) => setContractParamsForm(prev => ({ ...prev, spese_allaccio: e.target.value }))}
                  fullWidth
                  size="small"
                  helperText="0 = Non Dovute"
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  label="Durata Contratto (anni)"
                  type="number"
                  inputProps={{ step: '1', min: 1, max: 30 }}
                  value={contractParamsForm.durata_contratto_anni}
                  onChange={(e) => setContractParamsForm(prev => ({ ...prev, durata_contratto_anni: e.target.value }))}
                  fullWidth
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 1 }}>
                  Dati Fornitura
                </Typography>
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  label="Litri Stimati/Anno"
                  type="number"
                  inputProps={{ step: '100', min: 0 }}
                  value={contractParamsForm.litri_stimati_anno}
                  onChange={(e) => setContractParamsForm(prev => ({ ...prev, litri_stimati_anno: e.target.value }))}
                  fullWidth
                  size="small"
                />
              </Grid>
              
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Uso Fornitura</InputLabel>
                  <Select
                    value={contractParamsForm.uso_fornitura}
                    onChange={(e) => setContractParamsForm(prev => ({ ...prev, uso_fornitura: e.target.value }))}
                    label="Uso Fornitura"
                  >
                    <MenuItem value="">Seleziona...</MenuItem>
                    {CONTRACT_USAGE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContractParamsDialogOpen(false)} disabled={contractParamsLoading}>
            Annulla
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={contractParamsLoading ? <CircularProgress size={16} color="inherit" /> : <BuildIcon />}
            disabled={contractParamsLoading || !contractParamsMeterId}
            onClick={handleSaveContractParams}
          >
            Salva Parametri
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog conferma eliminazione definitiva */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleteLoading && setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          Elimina Cliente Definitivamente
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Attenzione!</strong> Questa operazione è irreversibile.
          </Alert>
          <Typography variant="body1" gutterBottom>
            Stai per eliminare definitivamente il cliente:
          </Typography>
          <Typography variant="h6" fontWeight="bold" sx={{ my: 1 }}>
            {fullName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Verranno eliminati anche tutti i dati associati: contatori, contratti, acconti e documenti.
          </Typography>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDeleteDialogOpen(false);
              setDeleteError(null);
            }} 
            disabled={deleteLoading}
          >
            Annulla
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={deleteLoading ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
            disabled={deleteLoading}
            onClick={handleDeletePermanent}
          >
            {deleteLoading ? 'Eliminazione...' : 'Elimina Definitivamente'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Invio Email Piano Rateizzazione */}
      <Dialog
        open={installmentEmailDialogOpen}
        onClose={() => !installmentEmailSending && setInstallmentEmailDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { minHeight: isMobile ? undefined : '60vh' } }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SendIcon color="primary" />
              <Typography variant="h6">Invia Piano di Rateizzazione via Email</Typography>
            </Box>
            <Button size="small" onClick={() => setInstallmentEmailDialogOpen(false)} disabled={installmentEmailSending}>
              Chiudi
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {installmentEmailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }} color="text.secondary">Caricamento anteprima...</Typography>
            </Box>
          ) : (
            <>
              {installmentEmailError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setInstallmentEmailError(null)}>
                  {installmentEmailError}
                </Alert>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Info piano */}
                <Alert severity="info" icon={<EventRepeatIcon />}>
                  <Typography variant="body2">
                    Piano #{installmentEmailPlanId}
                    {installmentEmailPreview?.customer_name && <> — <strong>{installmentEmailPreview.customer_name}</strong></>}
                    {' '} — In allegato verranno inclusi la lettera di rateizzazione e i bollettini di pagamento.
                  </Typography>
                </Alert>

                {/* Destinatario */}
                <TextField
                  label="A (Destinatario)"
                  value={installmentEmailAddress}
                  onChange={(e) => setInstallmentEmailAddress(e.target.value)}
                  fullWidth
                  required
                  type="email"
                  helperText={
                    (installmentEmailPreview?.email_secondaria || customer?.email_secondaria)
                      ? `Email secondaria disponibile: ${installmentEmailPreview?.email_secondaria || customer?.email_secondaria}`
                      : 'Indirizzo email del destinatario'
                  }
                  InputProps={{
                    endAdornment: (installmentEmailPreview?.email_secondaria || customer?.email_secondaria) ? (
                      <Button
                        size="small"
                        onClick={() => setInstallmentEmailAddress(installmentEmailPreview?.email_secondaria || customer.email_secondaria)}
                        sx={{ whiteSpace: 'nowrap', minWidth: 'auto', fontSize: '0.7rem' }}
                      >
                        Usa secondaria
                      </Button>
                    ) : null
                  }}
                />

                {/* Oggetto */}
                <TextField
                  label="Oggetto"
                  value={installmentEmailSubject}
                  onChange={(e) => setInstallmentEmailSubject(e.target.value)}
                  fullWidth
                  required
                />

                {/* Allegati */}
                {installmentEmailPreview?.attachments && installmentEmailPreview.attachments.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Allegati ({installmentEmailPreview.attachments.length})
                    </Typography>
                    {installmentEmailPreview.attachments.map((att, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                        <PdfIcon color="error" fontSize="small" />
                        <Typography variant="body2" color="text.secondary">
                          {att.filename} — {att.description}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                <Divider />

                {/* Anteprima messaggio */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Anteprima Messaggio
                  </Typography>
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                      bgcolor: 'background.paper',
                      maxHeight: '300px',
                      overflow: 'auto',
                    }}
                    dangerouslySetInnerHTML={{ __html: installmentEmailBody }}
                  />
                </Box>

                {/* Corpo modificabile (HTML) */}
                <TextField
                  label="Corpo Email (HTML)"
                  value={installmentEmailBody}
                  onChange={(e) => setInstallmentEmailBody(e.target.value)}
                  multiline
                  rows={6}
                  fullWidth
                  helperText="Puoi modificare il corpo dell'email (HTML). Le modifiche si riflettono nell'anteprima sopra."
                />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setInstallmentEmailDialogOpen(false)}
            disabled={installmentEmailSending}
          >
            Annulla
          </Button>
          <Button
            variant="contained"
            startIcon={installmentEmailSending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            disabled={installmentEmailLoading || installmentEmailSending || !installmentEmailAddress}
            onClick={async () => {
              setInstallmentEmailSending(true);
              setInstallmentEmailError(null);
              try {
                const payload = {
                  email: installmentEmailAddress,
                  subject: installmentEmailSubject || undefined,
                  body_html: installmentEmailBody || undefined,
                };
                const response = await customersApi.sendInstallmentPlanEmail(id, installmentEmailPlanId, payload);
                const msg = response.data?.message || `Piano inviato a ${installmentEmailAddress}`;
                alert(msg);
                setInstallmentEmailDialogOpen(false);
              } catch (err) {
                console.error('Errore invio email piano:', err);
                const errorMsg = err.response?.data?.message || 'Errore durante l\'invio dell\'email';
                setInstallmentEmailError(errorMsg);
              } finally {
                setInstallmentEmailSending(false);
              }
            }}
          >
            {installmentEmailSending ? 'Invio in corso...' : 'Invia Email'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default CustomerDetail;
